<template>
	<div class="chat-box" :class="{ 'mobile-fullscreen-mode': isMobile }" @click="closeRefBox()" @mousemove="readedMessage()">
		<!-- 拖拽覆盖层 -->
		<div v-if="isDragOver" class="drag-overlay">
			<div class="drag-content">
				<i class="el-icon-upload2"></i>
				<p>拖拽文件到此处发送</p>
				<p class="drag-tip">支持图片、文档等文件类型</p>
			</div>
		</div>
		<el-container>
			<el-header height="50px" class="chat-header" v-show="!isMobile || !showAgentIframe">
				<!-- 左侧标题 -->
				<div class="chat-title">
					<template v-if="currentAgentChat">
						<!-- 智能体会话：显示可点击的层级结构 -->
						<span class="parent-name clickable" @click="returnToParentChat" title="点击返回父级对话">
							{{ parentChatName }}
						</span>
						<span class="layer-separator">›</span>
						<span class="agent-name">{{ currentAgentChat.agentName }}</span>
					</template>
					<template v-else>
						<!-- 普通会话，直接显示标题 -->
						{{ title }}
					</template>
				</div>
				
				<!-- 右侧操作区 -->
				<div class="header-right">
					<!-- 智能体快速访问 - 新的实现：固定最右侧头像 -->
					<!-- mobile模式下隐藏，因为智能体iframe占据全屏 -->
					<div
						class="agent-avatars-container"
						:class="{ collapsed: showSide || showAgentSide }"
						v-if="recentAgents.length > 0 && !isMobile"
					>
						<!-- 可动画的头像容器（除了最后一个） -->
						<div 
							class="agent-avatars-animated"
							:class="{ expanded: avatarsExpanded }"
							:style="{ '--avatar-count': Math.min(recentAgents.length, 5) }"
							@mouseenter="expandAvatars"
							@mouseleave="collapseAvatars"
						>
							<div
								v-for="(agent, index) in recentAgents.slice(0, Math.min(recentAgents.length - 1, 4))"
								:key="agent.agentId"
								class="agent-avatar-mini"
								:style="{ '--avatar-index': index }"
								@mouseenter="handleAvatarMouseEnter(agent, $event)"
								@mouseleave="hideAgentCard"
								@click="quickStartAgentChat(agent)"
							>
								<img v-if="agent.avatarUrl" :src="agent.avatarUrl" :alt="agent.agentName" />
								<div v-else class="agent-avatar-text">{{ agent.agentName.charAt(0) }}</div>
							</div>
						</div>
						
						<!-- 固定的最右侧头像（永不移动） -->
						<div 
							class="agent-avatar-fixed"
							@mouseenter="expandAvatars"
							@mouseleave="collapseAvatars"
						>
							<div 
								class="agent-avatar-mini"
								@mouseenter="handleAvatarMouseEnter(recentAgents[Math.min(recentAgents.length - 1, 4)], $event)"
								@mouseleave="hideAgentCard"
								@click="quickStartAgentChat(recentAgents[Math.min(recentAgents.length - 1, 4)])"
							>
								<img v-if="recentAgents[Math.min(recentAgents.length - 1, 4)].avatarUrl"
									:src="recentAgents[Math.min(recentAgents.length - 1, 4)].avatarUrl"
									:alt="recentAgents[Math.min(recentAgents.length - 1, 4)].agentName" />
								<div v-else class="agent-avatar-text">
									{{ recentAgents[Math.min(recentAgents.length - 1, 4)].agentName.charAt(0) }}
								</div>
							</div>
						</div>
					</div>
					
					<!-- 更多按钮 -->
					<i class="el-icon-more header-btn" title="更多" @click="toggleSidePanel"></i>
				</div>
			</el-header>
			
			<!-- 智能体悬停卡片 -->
			<div v-if="hoveredAgentCard" 
				class="agent-hover-panel" 
				:style="agentCardStyle"
				@mouseenter="onPanelEnter"
				@mouseleave="onPanelLeave">
				<!-- 指向箭头 -->
				<div class="panel-arrow"></div>
				<!-- 使用统一的卡片组件 -->
				<chat-agent-card 
					:agent="hoveredAgentCard.agent"
					:show-actions="true"
					@start-chat="quickStartAgentChat">
				</chat-agent-card>
			</div>
			
			<!-- OpenMe 机器人管理面板 -->
			<div v-if="isOpenMeTerminal && !showOpenMeIframe" class="openme-panel">
				<el-radio-group v-if="hasCloudPc" v-model="useCloudPc" size="small" style="margin-bottom:8px;">
					<el-radio-button :label="false">本地</el-radio-button>
					<el-radio-button :label="true">云端</el-radio-button>
				</el-radio-group>
				<el-button type="primary" @click="startOpenMeSession">开始机器人会话</el-button>
			</div>
			
			<el-main style="padding: 0;">
				<el-container>
					<el-container class="content-box">
						<!-- OpenMe iframe 模式 -->
						<div v-if="showOpenMeIframe" class="openme-iframe-container">
							<iframe
								:src="openmeIframeUrl"
								class="openme-iframe"
								frameborder="0"
								allowfullscreen>
							</iframe>
						</div>
						<!-- 智能体iframe模式 - iframe 始终加载以保持与 happy 的通信，通过 CSS 控制显示/隐藏 -->
						<div
							ref="agentIframeContainer"
							class="agent-iframe-container"
							:class="{
								'iframe-hidden': (!showAgentIframe || !currentAgentChat) && !agentInitProgress.show,
								'mobile-mode': isMobile
							}">
							<iframe
								ref="agentIframe"
								:src="agentIframeUrl"
								class="agent-iframe"
								frameborder="0"
								allowfullscreen
								allow="microphone; camera"
								@load="onIframeLoad">
							</iframe>
							<!-- 🔥 会话切换：简单loading遮罩 -->
							<div v-if="iframeLoading && showAgentIframe && !agentInitProgress.show"
								class="iframe-loading-mask">
								<Spinner
									size="small"
									:text="isMobile ? '切换中...' : '切换智能体会话...'" />
							</div>
							<!-- 智能体初始化流程面板 -->
							<AgentInitProgressPanel
								:show="agentInitProgress.show"
								:steps="agentInitProgress.steps"
								:current-step="agentInitProgress.currentStep"
								:is-mobile="isMobile"
								@retry="handleInitRetry"
								@cancel="handleInitCancel"
												@close="handleInitClose" />
						</div>
						<!-- 普通消息模式 - 智能体 iframe 模式下隐藏 -->
						<el-main v-show="!showAgentIframe || !currentAgentChat" class="im-chat-main" id="chatScrollBox" @scroll="onScroll">
							<div class="im-chat-box">
								<!-- 调试：检查消息是否真的渲染 -->
								<div v-if="currentAgentChat && false" style="position: fixed; top: 100px; right: 10px; width: 300px; background: yellow; z-index: 9999; padding: 10px; border: 2px solid red;">
									<p>调试面板：有{{ displayMessages.length }}条消息</p>
									<div v-for="(msg, i) in displayMessages" :key="`debug-${i}`">
										<p>消息{{ i }}: {{ msg.selfSend ? '我' : '智能体' }} - {{ msg.content?.substring(0, 30) }}</p>
									</div>
								</div>
								<ul>
									<!-- 智能体会话消息 - 简化渲染用于调试 -->
									<template v-if="currentAgentChat">
										<li v-for="(msgInfo, idx) in displayMessages" :key="msgInfo.tmpId || msgInfo.id || `agent-${idx}`">
											<chat-message-item
												@call="onCall(msgInfo.type)"
												:mine="isMineMessage(msgInfo)"
												:headImage="headImage(msgInfo) || ''"
												:showName="showName(msgInfo) || '未知'"
												:msgInfo="msgInfo"
												:groupMembers="[]"
												@delete="deleteMessage"
												@recall="recallMessage"
												@quote="handleQuote"
												@task-completed="onTaskCompleted"
												@message-updated="onMessageUpdated"
												@open-forward-detail="openForwardDetail"
												@open-quote-detail="openQuoteDetail"
												@open-agent-task-detail="handleOpenAgentTaskDetail">
											</chat-message-item>
										</li>
									</template>

									<!-- 普通会话消息 -->
									<template v-else>
										<li v-for="(msgInfo, idx) in displayMessages" :key="msgInfo.tmpId || msgInfo.id || idx">
											<!-- LLM流式消息 - 输出和chat-message-item完全一样的DOM结构 -->
											<LLMStreamMessage
												v-if="msgInfo.type === 'llm-stream' && idx >= showMinIdx"
												:ref="`llm-stream-${msgInfo.streamId}`"
												:stream-id="msgInfo.streamId"
												:mine="isMineMessage(msgInfo)"
												:head-image="headImage(msgInfo)"
												:show-name="showName(msgInfo)"
												:msg-info="msgInfo"
												@completed="onLLMStreamCompleted"
												@error="onLLMStreamError"
												@need-scroll="scrollToBottom">
											</LLMStreamMessage>
											<!-- 普通消息 -->
											<chat-message-item v-else-if="idx >= showMinIdx"
												@call="onCall(msgInfo.type)"
												:mine="isMineMessage(msgInfo)" :headImage="headImage(msgInfo)"
												:showName="showName(msgInfo)" :msgInfo="msgInfo"
												:groupMembers="groupMembers" @delete="deleteMessage"
												@recall="recallMessage"
												@quote="handleQuote"
												@message-updated="onMessageUpdated"
												@open-forward-detail="openForwardDetail"
												@open-quote-detail="openQuoteDetail"
												@open-agent-task-detail="handleOpenAgentTaskDetail">
											</chat-message-item>
										</li>
									</template>
								</ul>
							</div>
						</el-main>

						<!-- 水平分隔条 - 消息和输入框之间 -->
						<div
							v-if="!showAgentIframe"
							class="input-resizer"
							@mousedown="handleInputResizerMouseDown"
							:class="{ 'resizer-dragging': isInputDragging, 'resizer-hovered': isInputResizerHovered }"
							@mouseenter="isInputResizerHovered = true"
							@mouseleave="onInputResizerMouseLeave"
						>
							<!-- 分割线 - 悬停时显示 -->
							<div class="resizer-line"></div>
							<!-- 三个小点手柄 - 始终显示，横向排列 -->
							<div class="resizer-handle">
								<div class="resizer-dot"></div>
								<div class="resizer-dot"></div>
								<div class="resizer-dot"></div>
							</div>
						</div>

						<!-- 🔥 多选模式操作栏 -->
						<MultiSelectActionBar
							v-if="isMultiSelectMode"
							:selectedCount="selectedCount"
							@cancel="exitMultiSelectMode"
							@forward="handleForward"
							@quote="handleMultiQuote"
						/>

						<el-footer :height="inputBoxHeight + 'px'" class="im-chat-footer" v-if="!showAgentIframe && !isMultiSelectMode">
							<div class="chat-tool-bar">
								<div title="表情" class="icon iconfont icon-emoji" ref="emotion"
									@click.stop="showEmotionBox()">
								</div>
								<div title="发送图片">
									<file-upload :action="'/image/upload'" :maxSize="100 * 1024 * 1024"
										:fileTypes="['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'image/gif']"
										@before="onImageBefore" @success="onImageSuccess" @fail="onImageFail">
										<i class="el-icon-picture-outline"></i>
									</file-upload>
								</div>
								<div title="发送文件">
									<file-upload ref="fileUpload" :action="'/file/upload'" :maxSize="10 * 1024 * 1024"
										@before="onFileBefore" @success="onFileSuccess" @fail="onFileFail">
										<i class="el-icon-wallet"></i>
									</file-upload>
								</div>
								<div title="回执消息" v-show="isGroup && memberSize <= 500"
									class="icon iconfont icon-receipt" :class="isReceipt ? 'chat-tool-active' : ''"
									@click="onSwitchReceipt">
								</div>
								<div title="发送语音" class="el-icon-microphone" @click="showRecordBox()">
								</div>
								<div title="语音通话" v-show="isPrivate" class="el-icon-phone-outline"
									@click="showPrivateVideo('voice')">
								</div>
								<div title="语音通话" v-show="isGroup" class="el-icon-phone-outline"
									@click="onGroupVideo()">
								</div>
								<div title="视频通话" v-show="isPrivate" class="el-icon-video-camera"
									@click="showPrivateVideo('video')">
								</div>
								<div title="聊天记录" class="el-icon-chat-dot-round" @click="showHistoryBox()"></div>
							</div>
							<div class="send-content-area">
								<ChatInput
									ref="chatInput"
									:ownerId="chat.type === 'GROUP' ? (group.ownerId || mine.id) : mine.id"
									:targetUserId="chat.type === 'PRIVATE' ? chat.targetId : null"
									:group-members="groupMembers"
									:agents="recentAgents"
									@submit="sendMessage"
									@input-focus="hideAgentSidePanel"
									@input-click="hideAgentSidePanel" />
								<!-- 发送按钮已隐藏，使用快捷键发送消息（Enter或Ctrl+Enter） -->
								<!-- <div class="send-btn-area">
									<el-button type="primary" icon="el-icon-s-promotion"
										@click="notifySend()">发送</el-button>
								</div> -->
							</div>
						</el-footer>
					</el-container>
				</el-container>

				<!-- 统一侧边栏：改为浮动面板，不占用布局空间 -->
				<transition name="slide-panel">
					<div class="floating-side-panel" v-if="showSide || showAgentSide">
						<!-- 群聊：显示Tab切换（智能体优先） -->
						<template v-if="chat.type === 'GROUP' && showSide">
							<el-tabs v-model="activeSideTab" type="card" class="side-tabs">
								<el-tab-pane label="智能体" name="agents">
									<chat-agent-side
										:chat="chat"
										:group="group"
										:groupMembers="groupMembers"
										:agents="recentAgents"
										@start-agent-chat="startAgentChat"
										@edit-agent="editAgent"
										@refresh-agents="loadRecentAgents">
									</chat-agent-side>
								</el-tab-pane>
								<el-tab-pane label="群成员" name="members">
									<chat-group-side
										:group="group"
										:groupMembers="groupMembers"
										@reload="loadGroup(group.id)">
									</chat-group-side>
								</el-tab-pane>
							</el-tabs>
						</template>
						<!-- 私聊：只显示智能体 -->
						<template v-else-if="showAgentSide">
							<chat-agent-side
								:chat="chat"
								:group="group"
								:groupMembers="groupMembers"
								:agents="recentAgents"
								@start-agent-chat="startAgentChat"
								@edit-agent="editAgent"
								@refresh-agents="loadRecentAgents">
							</chat-agent-side>
						</template>
					</div>
				</transition>
			</el-main>
			<emotion ref="emoBox" @emotion="onEmotion"></Emotion>
			<chat-record :visible="showRecord" @close="closeRecordBox" @send="onSendRecord"></chat-record>
			<group-member-selector ref="rtcSel" :group="group" @complete="onInviteOk"></group-member-selector>
			<rtc-group-join ref="rtcJoin" :groupId="group.id"></rtc-group-join>
			<chat-history :visible="showHistory" :chat="chat" :friend="friend" :group="group"
				:groupMembers="groupMembers" @close="closeHistoryBox"></chat-history>
		<!-- 转发消息对话框 -->
		<ForwardMessageDialog
			:visible.sync="showForwardDialog"
			:messages="forwardMessages"
			@forward="handleForwardConfirm"
		/>
		<!-- 转发消息详情查看 -->
		<ForwardMessageDetail
			:visible="showForwardDetail"
			:forwardData="currentForwardData"
			@close="closeForwardDetail"
		/>
		
		<!-- 🔥 简化的任务详情面板 -->
		<el-drawer
			:visible.sync="taskDetailPanel.visible"
			direction="rtl"
			size="60%"
			:with-header="false"
			:modal="true"
			:close-on-click-modal="true"
			:close-on-press-escape="true"
			class="task-detail-drawer"
			@close="closeTaskDetailPanel">
			
			<div class="task-detail-panel">
				<!-- 面板头部 -->
				<div class="panel-header">
					<h3 class="panel-title">
						<i class="el-icon-view"></i>
						任务执行详情
					</h3>
					<el-button 
						type="text" 
						icon="el-icon-close" 
						@click="closeTaskDetailPanel"
						class="close-btn">
					</el-button>
				</div>
				
				<!-- iframe容器 - 用于移动智能体iframe -->
				<div class="iframe-container" ref="taskDetailIframeContainer">
					<!-- iframe 会通过 DOM 操作移动到这里 -->
				</div>
			</div>
		</el-drawer>

		<!-- 🔥 智能体任务详情面板 -->
		<AgentTaskDetailPanel
			:visible.sync="agentTaskDetailPanelVisible"
			:taskStatusData="currentTaskStatusData"
		/>
		</el-container>
	</div>
</template>

<script>
import ChatGroupSide from "./ChatGroupSide.vue";
import ChatAgentSide from "./ChatAgentSide.vue";
import ChatAgentCard from "./ChatAgentCard.vue";
import ChatMessageItem from "./ChatMessageItem.vue";
import FileUpload from "../common/FileUpload.vue";
import Emotion from "../common/Emotion.vue";
import ChatRecord from "./ChatRecord.vue";
import ChatHistory from "./ChatHistory.vue";
import ForwardMessageDialog from "./ForwardMessageDialog.vue";
import ForwardMessageDetail from "./ForwardMessageDetail.vue";
import ChatAtBox from "./ChatAtBox.vue"
import GroupMemberSelector from "../group/GroupMemberSelector.vue"
import RtcGroupJoin from "../rtc/RtcGroupJoin.vue"
import ChatInput from "./ChatInput";
import LLMStreamMessage from "./LLMStreamMessage";
import Spinner from "../common/Spinner.vue";
import AgentInitProgressPanel from "./AgentInitProgressPanel.vue";
import MultiSelectActionBar from "./MultiSelectActionBar.vue";
import AgentTaskDetailPanel from "./AgentTaskDetailPanel.vue";
import { HappyIframeBridge } from '@/utils/HappyIframeBridge';
import { getAgentMentionManager, destroyAgentMentionManager } from '@/utils/AgentMentionManager';
import { autoSyncUserChats, ensurePodReady } from '@/api/vncApi.js';
import { createQuotedMessageContent } from '@/api/messageStructures';
import { MESSAGE_STATUS } from '@/api/enums';

export default {
	name: "chatPrivate",
	components: {
		ChatInput,
		ChatMessageItem,
		FileUpload,
		ChatGroupSide,
		ChatAgentSide,
		ChatAgentCard,
		Emotion,
		ChatRecord,
		ChatHistory,
		ChatAtBox,
		GroupMemberSelector,
		RtcGroupJoin,
		LLMStreamMessage,
		Spinner,
		AgentInitProgressPanel,
		MultiSelectActionBar,
		ForwardMessageDialog,
		ForwardMessageDetail,
		AgentTaskDetailPanel
	},
	props: {
		chat: {
			type: Object
		}
	},
	data() {
		return {
			userInfo: {},
			group: {},
			groupMembers: [],
			sendImageUrl: "",
			sendImageFile: "",
			placeholder: "",
			isReceipt: true,
			showRecord: false, // 是否显示语音录制弹窗
			showSide: false, // 是否显示侧边栏
			showAgentSide: false, // 是否显示智能体侧边栏
			showHistory: false, // 是否显示历史聊天记录
			showForwardDialog: false, // 是否显示转发对话框
			forwardMessages: [], // 要转发的消息列表
			showForwardDetail: false, // 是否显示转发消息详情
			currentForwardData: null, // 当前查看的转发消息数据
			activeSideTab: 'agents', // 侧边栏活动tab（默认显示智能体）
			showAgentIframe: false, // 是否显示智能体iframe模式
			iframeLoading: false, // iframe 加载状态
			currentAgentChat: null, // 当前激活的智能体会话（iframe模式）
			showOpenMeIframe: false, // 是否显示 OpenMe iframe
			hasCloudPc: false, // 是否有云电脑订阅
			useCloudPc: false, // 是否使用云端 openme
			openmeSessionStarted: false, // 用户是否已启动过 OpenMe 会话（用于记忆状态）
			openmeTerminalOnline: false, // OpenMe 终端是否在线
			// 智能体初始化状态
			agentInitProgress: {
				show: false, // 是否显示初始化面板
				steps: [
					{ key: 'userInfo', label: '用户信息加载', status: 'pending', error: null },
					{ key: 'agentInfo', label: '智能体信息获取', status: 'pending', error: null },
					{ key: 'vncPod', label: '云电脑环境准备', status: 'pending', error: null },
					{ key: 'session', label: '会话环境初始化', status: 'pending', error: null },
					{ key: 'complete', label: '智能体启动完成', status: 'pending', error: null }
				],
				currentStep: 0
			},
			iframeBridge: null, // Happy iframe 通信桥接实例
			lastSentImSessionInfo: null, // 上次发送的 IM 会话信息（用于避免重复发送）
			streamingMessageId: null,  // 当前流式消息ID
			isInitializingAgent: false, // 是否正在初始化智能体（防止重复初始化）
			recentAgents: [], // 最近使用的智能体列表
			loadAgentsTimer: null, // 防抖定时器
			isLoadingAgents: false, // 是否正在加载智能体
			hoveredAgentCard: null, // 悬停的智能体卡片
			hoverCardTimeout: null, // 悬停卡片隐藏定时器
			avatarsExpanded: false, // 头像是否展开
			avatarsFullyExpanded: false, // 头像是否完全展开（动画完成）
			avatarExpandTimeout: null, // 头像展开延迟定时器
			avatarFullyExpandTimeout: null, // 头像完全展开定时器
			pendingHoverCard: null, // 待显示的hover卡片
			hoverCheckTimeout: null, // hover检查定时器
			lockMessage: false, // 是否锁定发送，
			showMinIdx: 0, // 下标低于showMinIdx的消息不显示，否则页面会很卡置
			reqQueue: [],
			isSending: false,
			// LLM流式消息相关
			activeLLMStream: null, // 当前活跃的LLM流
			// 智能滚动相关
			isUserScrolledUp: false, // 用户是否向上滚动离开底部
			scrollThreshold: 100, // 距离底部多少像素内视为"接近底部"（阈值）
			llmStreams: new Map(), // 存储所有的LLM流，key为streamId
			// 无缝任务续问功能
			lastCompletedTask: null, // 最后完成的任务信息 {name, id}
			// 拖拽相关状态
			isDragOver: false,
			dragCounter: 0,
			// 输入框拖拽调整高度相关
			inputBoxHeight: this.getInitialInputHeight(), // 输入框高度
			isInputDragging: false, // 是否正在拖拽输入框分隔条
			isInputResizerHovered: false, // 输入框分隔条是否悬停
			inputDragStartY: 0, // 拖拽起始Y坐标
			inputDragStartHeight: 0, // 拖拽起始高度
			// 自动启动相关
			pendingAutoStart: null, // 待启动的智能体ID（当用户信息未就绪时暂存）
			
			// 🔥 智能体任务详情面板
			agentTaskDetailPanelVisible: false,
			currentTaskStatusData: null,
			
			// 🔥 简化的任务详情面板
			taskDetailPanel: {
				visible: false
			}
		}
	},
	methods: {
		// 检查 OpenMe 终端在线状态（通过设备表）
		async checkOpenMeTerminalStatus() {
			try {
				const userId = this.mine && this.mine.id;
				if (!userId) return false;
				const devices = await this.$http.get(`/device/list/${userId}`);
				const list = Array.isArray(devices) ? devices : [];
				this.openmeTerminalOnline = list.some(d => d.status === 'online');
				return this.openmeTerminalOnline;
			} catch (error) {
				console.error('获取设备状态失败:', error);
				return false;
			}
		},
		
		// 开始 OpenMe 机器人会话
		startOpenMeSession() {
			this.showOpenMeIframe = true;
			this.openmeSessionStarted = true;
		},
		
		// 关闭 OpenMe 机器人会话
		closeOpenMeSession() {
			this.showOpenMeIframe = false;
			this.openmeSessionStarted = false;
		},
		
		// 获取初始输入框高度
		getInitialInputHeight() {
			// 尝试从 localStorage 读取保存的高度
			const savedHeight = localStorage.getItem('chatInputBoxHeight');
			if (savedHeight) {
				return parseInt(savedHeight, 10);
			}
			// 默认高度 220px
			return 220;
		},

		// 输入框分隔条鼠标按下事件
		handleInputResizerMouseDown(e) {
			e.preventDefault();
			e.stopPropagation();
			this.isInputDragging = true;
			this.inputDragStartY = e.clientY;
			this.inputDragStartHeight = this.inputBoxHeight;

			// 添加全局鼠标事件监听
			document.addEventListener('mousemove', this.handleInputResizerMouseMove);
			document.addEventListener('mouseup', this.handleInputResizerMouseUp);

			// 设置拖拽时的样式
			document.body.style.cursor = 'row-resize';
			document.body.style.userSelect = 'none';
		},

		// 输入框分隔条鼠标移动事件
		handleInputResizerMouseMove(e) {
			if (!this.isInputDragging) return;
			e.preventDefault();

			// 注意：向上拖动是负数，向下拖动是正数，但我们需要反向（向上增大高度）
			const deltaY = this.inputDragStartY - e.clientY;
			const newHeight = this.inputDragStartHeight + deltaY;

			// 设置最小高度和最大高度
			const minHeight = 150; // 最小高度 150px
			const maxHeight = window.innerHeight * 0.6; // 最大高度为窗口高度的 60%

			// 限制高度范围
			this.inputBoxHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
		},

		// 输入框分隔条鼠标释放事件
		handleInputResizerMouseUp() {
			if (!this.isInputDragging) return;

			this.isInputDragging = false;

			// 移除全局事件监听
			document.removeEventListener('mousemove', this.handleInputResizerMouseMove);
			document.removeEventListener('mouseup', this.handleInputResizerMouseUp);

			// 恢复默认样式
			document.body.style.cursor = '';
			document.body.style.userSelect = '';

			// 保存高度到 localStorage
			localStorage.setItem('chatInputBoxHeight', this.inputBoxHeight.toString());
		},

		// 输入框分隔条鼠标离开事件
		onInputResizerMouseLeave() {
			if (!this.isInputDragging) {
				this.isInputResizerHovered = false;
			}
		},

		moveChatToTop() {
			let chatIdx = this.$store.getters.findChatIdx(this.chat);
			this.$store.commit("moveTop", chatIdx);
		},
		closeRefBox() {
			this.$refs.emoBox.close();
			// this.$refs.atBox.close();
		},
		onCall(type) {
			if (type == this.$enums.MESSAGE_TYPE.ACT_RT_VOICE) {
				this.showPrivateVideo('voice');
			} else if (type == this.$enums.MESSAGE_TYPE.ACT_RT_VIDEO) {
				this.showPrivateVideo('video');
			}
		},
		onSwitchReceipt() {
			this.isReceipt = !this.isReceipt;
			this.refreshPlaceHolder();
		},
		onImageSuccess(data, file) {
			let msgInfo = JSON.parse(JSON.stringify(file.msgInfo));
			msgInfo.content = JSON.stringify(data);
			msgInfo.receipt = this.isReceipt;
			this.sendMessageRequest(msgInfo).then((m) => {
				msgInfo.loadStatus = 'ok';
				msgInfo.id = m.id;
				this.isReceipt = false;
				this.$store.commit("insertMessage", [msgInfo, file.chat]);
			})
		},
		onImageFail(e, file) {
			let msgInfo = JSON.parse(JSON.stringify(file.msgInfo));
			msgInfo.loadStatus = 'fail';
			this.$store.commit("insertMessage", [msgInfo, file.chat]);
		},
		onImageBefore(file) {
			// 被封禁提示
			if (this.isBanned) {
				this.showBannedTip();
				return;
			}
			let url = URL.createObjectURL(file);
			let data = {
				originUrl: url,
				thumbUrl: url
			}
			let msgInfo = {
				id: 0,
				tmpId: this.generateId(),
				fileId: file.uid,
				sendId: this.mine.id,
				content: JSON.stringify(data),
				sendTime: new Date().getTime(),
				selfSend: true,
				type: 1,
				readedCount: 0,
				loadStatus: "loading",
				status: this.$enums.MESSAGE_STATUS.UNSEND
			}
			// 填充对方id
			this.fillTargetId(msgInfo, this.chat.targetId);
			// 插入消息
			this.$store.commit("insertMessage", [msgInfo, this.chat]);
			// 会话置顶
			this.moveChatToTop();
			// 滚动到底部
			this.scrollToBottom();
			// 借助file对象保存
			file.msgInfo = msgInfo;
			file.chat = this.chat;
		},
		onFileSuccess(url, file) {
			let data = {
				name: file.name,
				size: file.size,
				url: url
			}
			let msgInfo = JSON.parse(JSON.stringify(file.msgInfo));
			msgInfo.content = JSON.stringify(data);
			msgInfo.receipt = this.isReceipt
			this.sendMessageRequest(msgInfo).then((m) => {
				msgInfo.loadStatus = 'ok';
				msgInfo.id = m.id;
				this.isReceipt = false;
				this.refreshPlaceHolder();
				this.$store.commit("insertMessage", [msgInfo, file.chat]);
			})
		},
		onFileFail(e, file) {
			let msgInfo = JSON.parse(JSON.stringify(file.msgInfo));
			msgInfo.loadStatus = 'fail';
			this.$store.commit("insertMessage", [msgInfo, file.chat]);
		},
		onFileBefore(file) {
			// 被封禁提示
			if (this.isBanned) {
				this.showBannedTip();
				return;
			}
			let url = URL.createObjectURL(file);
			let data = {
				name: file.name,
				size: file.size,
				url: url
			}
			let msgInfo = {
				id: 0,
				tmpId: this.generateId(),
				sendId: this.mine.id,
				content: JSON.stringify(data),
				sendTime: new Date().getTime(),
				selfSend: true,
				type: 2,
				loadStatus: "loading",
				readedCount: 0,
				status: this.$enums.MESSAGE_STATUS.UNSEND
			}
			// 填充对方id
			this.fillTargetId(msgInfo, this.chat.targetId);
			// 插入消息
			this.$store.commit("insertMessage", [msgInfo, this.chat]);
			// 会话置顶
			this.moveChatToTop();
			// 滚动到底部
			this.scrollToBottom();
			// 借助file对象透传
			file.msgInfo = msgInfo;
			file.chat = this.chat;
		},
		onCloseSide() {
			this.showSide = false;
		},
		onScrollToTop() {
			// 多展示10条信息
			this.showMinIdx = this.showMinIdx > 10 ? this.showMinIdx - 10 : 0;
		},
		onScroll(e) {
			let scrollElement = e.target;
			let scrollTop;

			// 安全获取scrollTop属性
			if (window.vscodeVueChatUtils && window.vscodeVueChatUtils.safeGetProperty) {
				scrollTop = window.vscodeVueChatUtils.safeGetProperty(scrollElement, 'scrollTop');
			} else {
				scrollTop = scrollElement.scrollTop;
			}

			if (scrollTop < 30) { // 在顶部,不滚动的情况
				// 多展示20条信息
				this.showMinIdx = this.showMinIdx > 20 ? this.showMinIdx - 20 : 0;
			}

		// 智能滚动：检测用户是否接近底部
		const scrollHeight = scrollElement.scrollHeight;
		const clientHeight = scrollElement.clientHeight;
		const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

		if (distanceFromBottom <= this.scrollThreshold) {
			// 用户滚动到接近底部，恢复自动滚动
			if (this.isUserScrolledUp) {
				console.log('[ChatBox] 用户滚动到底部，恢复自动滚动');
				this.isUserScrolledUp = false;
			}
		} else if (distanceFromBottom > this.scrollThreshold && !this.isUserScrolledUp) {
			// 用户向上滚动离开底部，停止自动滚动
			console.log('[ChatBox] 用户向上滚动，停止自动滚动');
			this.isUserScrolledUp = true;
		}
		},
		showEmotionBox() {
			let width = this.$refs.emotion.offsetWidth;
			let left = this.$elm.fixLeft(this.$refs.emotion);
			let top = this.$elm.fixTop(this.$refs.emotion);
			this.$refs.emoBox.open({
				x: left + width / 2,
				y: top
			})
		},
		onEmotion(emoText) {
			this.$refs.chatInput.insertEmoji(emoText);
		},
		showRecordBox() {
			this.showRecord = true;
		},
		closeRecordBox() {
			this.showRecord = false;
		},
		showPrivateVideo(mode) {
			// 检查是否被封禁
			if (this.isBanned) {
				this.showBannedTip();
				return;
			}

			let rtcInfo = {
				mode: mode,
				isHost: true,
				friend: this.friend,
			}
			// 通过home.vue打开单人视频窗口
			this.$eventBus.$emit("openPrivateVideo", rtcInfo);
		},
		onGroupVideo() {
			// 检查是否被封禁
			if (this.isBanned) {
				this.showBannedTip();
				return;
			}
			// 邀请成员发起通话
			let ids = [this.mine.id];
			let maxChannel = this.$store.state.configStore.webrtc.maxChannel;
			this.$refs.rtcSel.open(maxChannel, ids, ids, []);
		},
		onInviteOk(members) {
			if (members.length < 2) {
				return;
			}
			let userInfos = [];
			members.forEach(m => {
				userInfos.push({
					id: m.userId,
					nickName: m.showNickName,
					headImage: m.headImage,
					isCamera: false,
					isMicroPhone: true
				})
			})
			let rtcInfo = {
				isHost: true,
				groupId: this.group.id,
				inviterId: this.mine.id,
				userInfos: userInfos
			}
			// 通过home.vue打开多人视频窗口
			this.$eventBus.$emit("openGroupVideo", rtcInfo);
		},
		showHistoryBox() {
			this.showHistory = true;
		},
		closeHistoryBox() {
			this.showHistory = false;
		},
		// 打开转发消息详情
		openForwardDetail(data) {
			this.currentForwardData = data.forwardData;
			this.showForwardDetail = true;
		},
		// 🔥 打开引用消息详情（复用转发详情组件）
		openQuoteDetail(data) {
			// 将引用消息转换为转发详情组件所需的格式
			this.currentForwardData = {
				title: '引用消息',
				messages: data.quotedMessages || []
			};
			this.showForwardDetail = true;
		},
		// 关闭转发消息详情
		closeForwardDetail() {
			this.showForwardDetail = false;
			this.currentForwardData = null;
		},
		onSendRecord(data) {
			// 检查是否被封禁
			if (this.isBanned) {
				this.showBannedTip();
				return;
			}
			let msgInfo = {
				content: JSON.stringify(data),
				type: 3,
				receipt: this.isReceipt
			}
			// 填充对方id
			this.fillTargetId(msgInfo, this.chat.targetId);
			this.sendMessageRequest(msgInfo).then((m) => {
				m.selfSend = true;
				this.$store.commit("insertMessage", [m, this.chat]);
				// 会话置顶
				this.moveChatToTop();
				// 保持输入框焦点
				this.$refs.chatInput.focus();
				// 滚动到底部
				this.scrollToBottom(true); // 用户发送消息，强制滚动
				// 关闭录音窗口
				this.showRecord = false;
				this.isReceipt = false;
				this.refreshPlaceHolder();
			})
		},
		fillTargetId(msgInfo, targetId) {
			if (this.chat.type == "GROUP") {
				msgInfo.groupId = targetId;
			} else if (this.chat.isTerminalChat || this.chat.isTerminalInbox) {
				// 🔧 修正：终端会话处理（支持新旧格式）
				const parts = targetId.split('_');
				const userId = parseInt(parts[0]);
				
				msgInfo.recvId = userId; // 用户自己的ID
				
				// 🔧 处理新的目标终端模式
				if (this.chat.isTerminalChat) {
					msgInfo.targetTerminal = this.chat.senderTerminal; // 发送给发送端终端
					msgInfo.senderTerminal = this.getCurrentTerminal(); // 当前终端作为发送端
				} 
				// 🔧 兼容旧的终端收件箱模式
				else if (this.chat.isTerminalInbox) {
					const receivingTerminal = parseInt(parts[1]);
					msgInfo.targetTerminal = receivingTerminal; // 保持兼容性
					msgInfo.senderTerminal = this.getCurrentTerminal();
				}
			} else {
				msgInfo.recvId = targetId;
				msgInfo.senderTerminal = this.getCurrentTerminal();
			}
		},
		notifySend() {
			this.$refs.chatInput.submit();
		},
		async sendMessage(fullList) {
			this.resetEditor();
			this.readedMessage();

			// 智能体 iframe 模式下由 Happy 负责输入
			if (this.currentAgentChat && this.showAgentIframe) {
				this.$message.warning('智能体对话请在智能体面板中进行');
				return;
			}

			// 检查是否被封禁
			if (this.isBanned) {
				this.showBannedTip();
				return;
			}

			// 🔥 获取引用消息（如果有）
			const quotedMessages = this.$store.state.chatStore.quotedMessages;
			let quotedData = null;
			if (quotedMessages && quotedMessages.active && quotedMessages.messages.length > 0) {
				// 构造引用数据，优先使用 sendNickName
				quotedData = quotedMessages.messages.map(msg => ({
					id: msg.id || msg.tmpId,
					senderId: msg.sendId,
					sendNickName: msg.sendNickName || msg.senderName || msg.showName || '未知',
					content: msg.content,
					type: msg.type,
					sendTime: msg.sendTime
				}));
				// 🔥 立即清空引用消息面板
				this.$store.commit('clearQuotedMessages');
			}

			let sendText = this.isReceipt ? "【回执消息】" : "";
			let promiseList = [];
			for (let i = 0; i < fullList.length; i++) {
				let msg = fullList[i];
				switch (msg.type) {
					case "text":
						// 🔥 传递引用数据和智能体提及到文本消息发送
						await this.sendTextMessage(sendText + msg.content, msg.atUserIds, quotedData, msg.agentMentions);
						break;
					case "image":
						await this.sendImageMessage(msg.content.file);
						break;
					case "file":
						await this.sendFileMessage(msg.content.file);
						break;
				}

			}

			// 触发消息发送完成事件
			this.$emit('message-sent');
		},
		sendImageMessage(file) {
			return new Promise((resolve, reject) => {
				this.onImageBefore(file);
				let formData = new FormData()
				formData.append('file', file)
				this.$http.post("/image/upload", formData, {
					headers: {
						'Content-Type': 'multipart/form-data'
					}
				}).then((data) => {
					this.onImageSuccess(data, file);
					resolve();
				}).catch((res) => {
					this.onImageFail(res, file);
					reject();
				})
				this.$nextTick(() => this.$refs.chatInput.focus());
				this.scrollToBottom(true); // 用户发送消息，强制滚动
			});
		},
		async sendTextMessage(sendText, atUserIds, quotedData, agentMentions) {
			// 移除零宽字符后检查
			const cleanText = sendText.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
			if (!cleanText && !(quotedData && quotedData.length > 0)) {
				return;
			}
			// 使用清理后的文本
			sendText = cleanText;

			// 检测@智能体提及 - 只有当 agentMentions 数组存在且不为空时才处理
			let pendingAgentTask = null;
			let taskId = null;
			let tempMessage = null;
			
			if (this.agentMentionManager && agentMentions && agentMentions.length > 0) {
				// 从 agentMentions 数组中获取第一个智能体（支持多个智能体时可扩展）
				const agentMention = agentMentions[0];
				
				console.log('[ChatBox] 检测到智能体提及:', agentMention);
				
				// 构造智能体任务数据
				pendingAgentTask = {
					agentId: agentMention.agentId,
					agentName: agentMention.agentName,
					taskContent: sendText, // 整个消息内容作为任务内容
					originalMessage: sendText
				};
				
				// 立即创建临时消息（UX优化：即时反馈）
				// Wait for the real IM message. The optimistic entry can race the
				// server update and leave two visible @ mentions.
				
				// 🔥 准备引用消息数据（如果有）
				let quotedMessages = null;
				if (quotedData && quotedData.length > 0) {
					quotedMessages = quotedData.map(msg => ({
						id: msg.id,
						senderId: msg.senderId,
						senderName: msg.sendNickName || msg.senderName || '未知',
						type: msg.type,
						content: msg.content,
						sendTime: msg.sendTime
					}));
				}
				
				// 异步调用 API 获取后端 taskId
				try {
					const taskResult = await this.agentMentionManager.sendAgentTask({
						agentId: pendingAgentTask.agentId,
						agentName: pendingAgentTask.agentName,
						taskContent: pendingAgentTask.taskContent,
						imContext: {
							userId: this.$store.state.userStore.userInfo?.id,
							sessionId: this.chat.targetId,
							sessionType: this.chat.type,
							userName: this.$store.state.userStore.userInfo?.nickName,
							chatName: this.chat.showName || this.chat.name,
							tokenKey: sessionStorage.getItem('tokenKey') || this.$store.state.userStore.userInfo?.tokenKey
						},
						quotedMessages,
						options: {
							yolo: true
						}
					});
					
					if (!taskResult || !taskResult.taskId) {
						tempMessage.status = MESSAGE_STATUS.FAIL;
						this.$message.error('智能体任务调用失败');
						return Promise.reject();
					}
					
					// 🔥 保存 taskId 和 shareUrl
					taskId = taskResult.taskId;
					pendingAgentTask.shareUrl = taskResult.shareUrl;
				} catch (error) {
					console.error('[AgentTask] API 调用失败:', error);
					if (tempMessage) {
						tempMessage.status = MESSAGE_STATUS.FAIL;
					}
					this.$message.error('智能体任务调用失败');
					return Promise.reject();
				}
			}

			return new Promise((resolve, reject) => {
				// 构建消息内容
				let content;
				if (pendingAgentTask && taskId) {
					// 使用后端 taskId 创建 AGENT_MENTION 消息
					const { createAgentMentionContent } = require('@/api/messageStructures');
					content = JSON.stringify(createAgentMentionContent(
						pendingAgentTask.taskContent || sendText,
						pendingAgentTask.agentId,
						pendingAgentTask.agentName,
						taskId,
						quotedData,
						pendingAgentTask.shareUrl // 🔥 传入 shareUrl
					));
					
					console.log('[【智能体状态debug】] 🎯 AGENT_MENTION 消息内容已生成');
				} else if (quotedData && quotedData.length > 0) {
					content = JSON.stringify(createQuotedMessageContent(sendText, quotedData));
				} else {
					content = sendText;
				}

				let msgInfo = {
					content: content,
					type: 0
				}
				// 填充对方id
				this.fillTargetId(msgInfo, this.chat.targetId);
				// 被@人员列表
				if (this.chat.type == "GROUP") {
					msgInfo.atUserIds = atUserIds;
					msgInfo.receipt = this.isReceipt;
				}

				this.lockMessage = true;
				this.sendMessageRequest(msgInfo).then((m) => {
					m.selfSend = true;
					
					// 如果有临时消息，替换它；否则正常插入
					if (tempMessage) {
						this.$store.commit("replaceMessage", {
							chat: this.chat,
							tmpId: tempMessage.tmpId,
							newMessage: m
						});
					} else {
						this.$store.commit("insertMessage", [m, this.chat]);
					}

					this.moveChatToTop();
				}).finally(() => {
					this.scrollToBottom(true);
					this.isReceipt = false;
					resolve();
				});
			});
		},
		sendFileMessage(file) {
			return new Promise((resolve, reject) => {
				let check = this.$refs.fileUpload.beforeUpload(file);
				if (check) {
					this.$refs.fileUpload.onFileUpload({ file });
				}
			})
		},
		/**
		 * 🔥 处理消息更新事件（用于文件自动下载标记等）
		 */
		onMessageUpdated(msgInfo) {
			console.log('[ChatBox] 消息已更新，触发保存:', msgInfo.id);
			// 标记 chat 需要保存
			if (this.chat) {
				this.chat.stored = false;
			}
		},

		// 🔥 多选模式相关方法
		exitMultiSelectMode() {
			this.$store.commit('exitMultiSelectMode');
		},

		// 🔥 单条消息引用处理
		handleQuote(msgInfo) {
			const chat = this.$store.state.chatStore.activeChat;
			if (!chat) {
				this.$message.warning('当前没有活跃会话');
				return;
			}

			// 🔥 补充发送者昵称（私聊中对方消息可能没有 sendNickName）
			const enrichedMessage = {
				...msgInfo,
				sendNickName: msgInfo.sendNickName || this.showName(msgInfo) || '未知'
			};

			// 设置引用消息
			this.$store.commit('setQuotedMessage', {
				message: enrichedMessage,
				chatId: chat.targetId
			});

			// 聚焦到输入框
			this.$nextTick(() => {
				if (this.$refs.chatInput) {
					this.$refs.chatInput.focus();
				}
			});

			console.log('[ChatBox] 设置单条引用消息:', enrichedMessage);
		},

		// 🔥 多条消息引用处理
		handleMultiQuote() {
			const selectedIds = this.$store.state.chatStore.multiSelectMode.selectedMessages;
			const chat = this.$store.state.chatStore.activeChat;

			if (!selectedIds || selectedIds.length === 0) {
				this.$message.warning('请先选择要引用的消息');
				return;
			}

			// 获取选中的消息
			const selectedMessages = chat.messages.filter(m =>
				selectedIds.includes(m.id || m.tmpId)
			);

			if (selectedMessages.length === 0) {
				this.$message.warning('未找到选中的消息');
				return;
			}

			// 🔥 补充发送者昵称（私聊中对方消息可能没有 sendNickName）
			const enrichedMessages = selectedMessages.map(msg => ({
				...msg,
				sendNickName: msg.sendNickName || this.showName(msg) || '未知'
			}));

			// 设置引用消息
			this.$store.commit('setQuotedMessages', {
				messages: enrichedMessages,
				chatId: chat.targetId
			});

			// 退出多选模式
			this.exitMultiSelectMode();

			// 聚焦到输入框
			this.$nextTick(() => {
				if (this.$refs.chatInput) {
					this.$refs.chatInput.focus();
				}
			});

			console.log('[ChatBox] 设置多条引用消息:', enrichedMessages.length);
		},

		async handleForward() {
			const selectedIds = this.$store.state.chatStore.multiSelectMode.selectedMessages;
			const chat = this.$store.state.chatStore.activeChat;

			if (!selectedIds || selectedIds.length === 0) {
				this.$message.warning('请先选择要转发的消息');
				return;
			}

			// 获取选中的消息
			const selectedMessages = chat.messages.filter(m =>
				selectedIds.includes(m.id || m.tmpId)
			);

			if (selectedMessages.length === 0) {
				this.$message.warning('未找到选中的消息');
				return;
			}

			// 存储要转发的消息并打开对话框
			this.forwardMessages = selectedMessages;
			this.showForwardDialog = true;
		},

		// 🔥 处理转发确认
		async handleForwardConfirm({ targetChats, messages, leaveMessage }) {
			try {
				// 兼容旧的单个会话转发格式
				const chatIds = Array.isArray(targetChats) ? targetChats : [targetChats];

				if (chatIds.length === 0) {
					this.$message.error("请选择目标会话");
					return;
				}

				// 批量转发到多个会话
				let successCount = 0;
				let failCount = 0;

				for (const targetChatId of chatIds) {
					try {
						// 找到目标会话
						const targetChat = this.$store.state.chatStore.chats.find(
							chat => chat.targetId === targetChatId
						);

						if (!targetChat) {
							console.warn(`目标会话不存在: ${targetChatId}`);
							failCount++;
							continue;
						}

						// 创建转发消息
						const forwardMessage = this.createForwardMessage(messages, this.chat);

						// 发送转发卡片消息
						await this.sendForwardMessageToChat(forwardMessage, targetChat);

						// 如果有留言，发送留言文本消息
						if (leaveMessage && leaveMessage.trim()) {
							await this.sendLeaveMessageToChat(leaveMessage.trim(), targetChat);
						}

						successCount++;
					} catch (error) {
						console.error(`[ChatBox] 转发到会话 ${targetChatId} 失败:`, error);
						failCount++;
					}
				}

				// 退出多选模式
				this.exitMultiSelectMode();

				// 显示结果提示
				if (successCount > 0 && failCount === 0) {
					this.$message.success(`已成功转发到 ${successCount} 个会话`);
				} else if (successCount > 0 && failCount > 0) {
					this.$message.warning(`成功转发到 ${successCount} 个会话，${failCount} 个失败`);
				} else {
					this.$message.error("转发失败，请重试");
				}
			} catch (error) {
				console.error("[ChatBox] 转发确认失败:", error);
				this.$message.error("转发失败，请重试");
			}
		},

		// 🔥 发送转发消息到指定会话
		async sendForwardMessageToChat(forwardMessage, targetChat) {
			// 创建消息对象
			const msgInfo = {
				tmpId: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
				sendId: this.mine.id,
				content: JSON.stringify(forwardMessage),
				type: this.$enums.MESSAGE_TYPE.TEXT,
				sendTime: Date.now(),
				selfSend: true,
				status: 0
			};

			// 根据目标会话类型填充ID
			if (targetChat.type === "GROUP") {
				msgInfo.groupId = targetChat.targetId;
			} else if (targetChat.isTerminalChat || targetChat.isTerminalInbox) {
				const parts = targetChat.targetId.split('_');
				const userId = parseInt(parts[0]);
				msgInfo.recvId = userId;

				if (targetChat.isTerminalChat) {
					msgInfo.targetTerminal = targetChat.senderTerminal;
					msgInfo.senderTerminal = this.getCurrentTerminal();
				} else if (targetChat.isTerminalInbox) {
					const receivingTerminal = parseInt(parts[1]);
					msgInfo.targetTerminal = receivingTerminal;
					msgInfo.senderTerminal = this.getCurrentTerminal();
				}
			} else {
				msgInfo.recvId = targetChat.targetId;
				msgInfo.senderTerminal = this.getCurrentTerminal();
			}

			// 根据目标会话类型选择正确的API
			let apiUrl;
			if (targetChat.isTerminalChat || targetChat.isTerminalInbox) {
				apiUrl = `/message/private/send`;
			} else {
				apiUrl = `/message/${targetChat.type.toLowerCase()}/send`;
			}

			// 发送消息
			const res = await this.$http({
				url: apiUrl,
				method: 'post',
				data: msgInfo
			});

			const sentMsg = res;
			sentMsg.selfSend = true;
			this.$store.commit("insertMessage", [sentMsg, targetChat]);

			// 目标会话置顶
			const chatIndex = this.$store.state.chatStore.chats.findIndex(
				c => c.targetId === targetChat.targetId
			);
			if (chatIndex > 0) {
				this.$store.commit("moveTop", chatIndex);
			}
		},

		// 🔥 发送留言文本消息到指定会话
		async sendLeaveMessageToChat(leaveMessage, targetChat) {
			// 创建文本消息对象
			const msgInfo = {
				tmpId: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
				sendId: this.mine.id,
				content: leaveMessage,
				type: this.$enums.MESSAGE_TYPE.TEXT,
				sendTime: Date.now(),
				selfSend: true,
				status: 0
			};

			// 根据目标会话类型填充ID
			if (targetChat.type === "GROUP") {
				msgInfo.groupId = targetChat.targetId;
			} else if (targetChat.isTerminalChat || targetChat.isTerminalInbox) {
				const parts = targetChat.targetId.split('_');
				const userId = parseInt(parts[0]);
				msgInfo.recvId = userId;

				if (targetChat.isTerminalChat) {
					msgInfo.targetTerminal = targetChat.senderTerminal;
					msgInfo.senderTerminal = this.getCurrentTerminal();
				} else if (targetChat.isTerminalInbox) {
					const receivingTerminal = parseInt(parts[1]);
					msgInfo.targetTerminal = receivingTerminal;
					msgInfo.senderTerminal = this.getCurrentTerminal();
				}
			} else {
				msgInfo.recvId = targetChat.targetId;
				msgInfo.senderTerminal = this.getCurrentTerminal();
			}

			// 根据目标会话类型选择正确的API
			let apiUrl;
			if (targetChat.isTerminalChat || targetChat.isTerminalInbox) {
				apiUrl = `/message/private/send`;
			} else {
				apiUrl = `/message/${targetChat.type.toLowerCase()}/send`;
			}

			// 发送消息
			const res = await this.$http({
				url: apiUrl,
				method: 'post',
				data: msgInfo
			});

			const sentMsg = res;
			sentMsg.selfSend = true;
			this.$store.commit("insertMessage", [sentMsg, targetChat]);
		},

		createForwardMessage(messages, chat) {
			// 按时间排序
			const sortedMessages = [...messages].sort((a, b) => a.sendTime - b.sendTime);

			// 提取消息数据
			const messageData = sortedMessages.map(msg => {
				const data = {
					id: msg.id || msg.tmpId,
					senderId: msg.sendId,
					senderName: msg.sendNickName || this.showName(msg) || '未知',
					senderAvatar: this.headImage(msg) || '',
					content: msg.content,
					type: msg.type,
					timestamp: msg.sendTime
				};

				// 添加媒体信息（图片、文件等）
				try {
					if (msg.type === 1) { // 图片
						const imageData = JSON.parse(msg.content);
						data.mediaUrl = imageData.originUrl;
						data.thumbUrl = imageData.thumbUrl;
					} else if (msg.type === 2) { // 文件
						const fileData = JSON.parse(msg.content);
						data.mediaUrl = fileData.url;
						data.fileName = fileData.name;
						data.fileSize = fileData.size;
					}
				} catch (e) {
					console.warn('[ChatBox] 解析消息内容失败:', e);
				}

				return data;
			});

			// 生成摘要
			const senderNames = [...new Set(messageData.map(m => m.senderName))];
			const summary = senderNames.length > 3
				? `${senderNames.slice(0, 3).join('、')}等人的聊天记录`
				: `${senderNames.join('、')}的聊天记录`;

			return {
				type: 'FORWARD',
				version: '1.0',
				title: '聊天记录',
				messageCount: messageData.length,
				messages: messageData,
				summary,
				createTime: Date.now()
			};
		},

		async sendForwardMessage(forwardMessage) {
			const chat = this.$store.state.chatStore.activeChat;

			// 创建消息对象
			const msgInfo = {
				tmpId: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
				sendId: this.mine.id,
				content: JSON.stringify(forwardMessage),
				type: this.$enums.MESSAGE_TYPE.TEXT,
				sendTime: Date.now(),
				selfSend: true,
				status: 0
			};

			// 填充对方id
			this.fillTargetId(msgInfo, chat.targetId);

			try {
				// 发送消息
				const sentMsg = await this.sendMessageRequest(msgInfo);
				sentMsg.selfSend = true;
				this.$store.commit("insertMessage", [sentMsg, chat]);

				// 会话置顶
				this.moveChatToTop();

				// 滚动到底部
				this.scrollToBottom(true);

				// 退出多选模式
				this.exitMultiSelectMode();

				this.$message.success('转发成功');
			} catch (error) {
				console.error('[ChatBox] 转发消息失败:', error);
				this.$message.error('转发失败，请重试');
			}
		},

		deleteMessage(msgInfo) {
			this.$confirm('确认删除消息?', '删除消息', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				this.$store.commit("deleteMessage", [msgInfo, this.chat]);
			});
		},
		recallMessage(msgInfo) {
			this.$confirm('确认撤回消息?', '撤回消息', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				let url = `/message/${this.chat.type.toLowerCase()}/recall/${msgInfo.id}`
				this.$http({
					url: url,
					method: 'delete'
				}).then((m) => {
					this.$message.success("消息已撤回");
					m.selfSend = true;
					this.$store.commit("recallMessage", [m, this.chat]);
				})
			});
		},
		readedMessage() {
			if (this.chat.unreadCount == 0) {
				return;
			}
			this.$store.commit("resetUnreadCount", this.chat)
			if (this.chat.type == "GROUP") {
				var url = `/message/group/readed?groupId=${this.chat.targetId}`
			} else {
				url = `/message/private/readed?friendId=${this.chat.targetId}`
			}
			this.$http({
				url: url,
				method: 'put'
			}).then(() => { })
		},
		loadReaded(fId) {
			// 跳过智能体会话（智能体ID包含"agent_"）
			if (fId && fId.toString().includes('agent_')) {
				return;
			}
			
			this.$http({
				url: `/message/private/maxReadedId?friendId=${fId}`,
				method: 'get'
			}).then((id) => {
				this.$store.commit("readedMessage", {
					friendId: fId,
					maxId: id
				});
			});
		},
		async loadGroup(groupId) {
			try {
				// 加载群组信息
				const group = await this.$http({
					url: `/group/find/${groupId}`,
					method: 'get'
				});
				this.group = group;
				this.$store.commit("updateChatFromGroup", group);
				this.$store.commit("updateGroup", group);

				// 加载群组成员
				const groupMembers = await this.$http({
					url: `/group/members/${groupId}`,
					method: 'get'
				});
				this.groupMembers = groupMembers;

				// 群组成员加载完成后，重新加载智能体列表
				console.log('[ChatBox] 群组成员加载完成，重新加载智能体列表');
				await this.loadRecentAgents();
			} catch (error) {
				console.error('[ChatBox] 加载群组信息失败:', error);
			}
		},
		updateFriendInfo() {
			if (this.isFriend) {
				// store的数据不能直接修改，深拷贝一份store的数据
				let friend = JSON.parse(JSON.stringify(this.friend));
				friend.headImage = this.userInfo.headImageThumb;
				friend.nickName = this.userInfo.nickName;
				friend.showNickName = friend.remarkNickName ? friend.remarkNickName : friend.nickName;
				this.$store.commit("updateChatFromFriend", friend);
				this.$store.commit("updateFriend", friend);
			} else {
				this.$store.commit("updateChatFromUser", this.userInfo);
			}
		},
		loadFriend(friendId) {
			// 跳过智能体会话（智能体ID包含"agent_"）
			if (friendId && friendId.toString().includes('agent_')) {
				// 对于智能体会话，设置一个默认的userInfo
				this.userInfo = {
					id: friendId,
					nickName: this.chat.showName || '智能体',
					headImageThumb: this.chat.headImage
				};
				return;
			}

			// 🔥 修复："我的电脑"会话的 targetId 格式为 userId_userId，需要解析出真实的用户 ID
			let actualUserId = friendId;
			if (friendId && friendId.toString().includes('_')) {
				const parts = friendId.toString().split('_');
				// "我的电脑"会话: targetId = userId_userId，取第一个
				actualUserId = parts[0];
				console.log('[ChatBox] 解析私聊 targetId:', friendId, '→ userId:', actualUserId);

				// 如果是"我的电脑"会话（两个 ID 相同），直接使用当前用户信息
				if (parts[0] === parts[1]) {
					console.log('[ChatBox] 检测到"我的电脑"会话，使用当前用户信息');
					this.userInfo = this.mine;
					this.updateFriendInfo();
					return;
				}
			}

			// 获取好友信息
			this.$http({
				url: `/user/find/${actualUserId}`,
				method: 'GET'
			}).then((userInfo) => {
				this.userInfo = userInfo;
				this.updateFriendInfo();
			})
		},
		showName(msgInfo) {
			if (!msgInfo) {
				return ""
			}
			// 如果消息有sendNickName字段，优先使用
			if (msgInfo.sendNickName) {
				return msgInfo.sendNickName;
			}
			if (this.isGroup) {
				let member = this.groupMembers.find((m) => m.userId == msgInfo.sendId);
				return member ? member.showNickName : msgInfo.sendNickName || "";
			} else {
				return msgInfo.selfSend ? this.mine.nickName : this.chat.showName
			}
		},
		headImage(msgInfo) {
			// 智能体会话特殊处理
			if (this.currentAgentChat) {
				if (msgInfo.selfSend) {
					return this.mine.headImageThumb || this.mine.headImage || "";
				} else {
					// 智能体头像：从 store 中查找对应的智能体 chat 对象
					const targetId = this.currentAgentChat.targetId;
					const agentChat = this.$store.state.chatStore.chats.find(c =>
						c.type === 'AGENT' && c.targetId === targetId
					);
					return agentChat?.headImage || "";
				}
			}

			if (this.isGroup) {
				let member = this.groupMembers.find((m) => m.userId == msgInfo.sendId);
				return member ? member.headImage : "";
			} else {
				return msgInfo.sendId == this.mine.id ? this.mine.headImageThumb : this.chat.headImage
			}
		},
		resetEditor() {
			this.$nextTick(() => {
				if (this.$refs.chatInput) {
					this.$refs.chatInput.clear();
					this.$refs.chatInput.focus();
				}
			});
		},
	/**
	 * 滚动到底部（智能滚动）
	 * @param {boolean} force - 是否强制滚动（忽略用户滚动状态）
	 */
	scrollToBottom(force = false) {
		// 智能滚动：只在用户没有向上滚动时才自动滚动
		if (!force && this.isUserScrolledUp) {
			console.log('[ChatBox] 用户已向上滚动，跳过自动滚动');
			return;
		}

		this.$nextTick(() => {
			let div = document.getElementById("chatScrollBox");
			if (div) {
				div.scrollTop = div.scrollHeight;
				// 滚动后重置用户滚动状态
				this.isUserScrolledUp = false;
			}
		});
	},
		refreshPlaceHolder() {
			if (this.isReceipt) {
				this.placeholder = "【回执消息】"
			} else if (this.$refs.editBox && this.$refs.editBox.innerHTML) {
				this.placeholder = ""
			} else {
				this.placeholder = "聊点什么吧~";
			}
		},
		sendMessageRequest(msgInfo) {
			return new Promise((resolve, reject) => {
				// 请求入队列，防止请求"后发先至"，导致消息错序
				this.reqQueue.push({ msgInfo, resolve, reject });
				this.processReqQueue();
			})
		},
		processReqQueue() {
			if (this.reqQueue.length && !this.isSending) {
				this.isSending = true;
				const reqData = this.reqQueue.shift();
				this.$http({
					url: this.messageAction,
					method: 'post',
					data: reqData.msgInfo
				}).then((res) => {
					reqData.resolve(res)
				}).catch((e) => {
					reqData.reject(e)
				}).finally(() => {
					this.isSending = false;
					// 发送下一条请求
					this.processReqQueue();
				})
			}
		},
		showBannedTip() {
			let msgInfo = {
				tmpId: this.generateId(),
				sendId: this.mine.id,
				sendTime: new Date().getTime(),
				type: this.$enums.MESSAGE_TYPE.TIP_TEXT
			}
			if (this.chat.type == "PRIVATE") {
				msgInfo.recvId = this.mine.id
				msgInfo.content = "该用户已被管理员封禁,原因:" + this.userInfo.reason
			} else {
				msgInfo.groupId = this.group.id;
				msgInfo.content = "本群聊已被管理员封禁,原因:" + this.group.reason
			}
			this.$store.commit("insertMessage", [msgInfo, this.chat]);
		},
		generateId() {
			// 生成临时id
			return String(new Date().getTime()) + String(Math.floor(Math.random() * 1000));
		},
		
		// 节流函数 - 优化性能
		throttle(func, delay) {
			let timeoutId;
			let lastExecTime = 0;
			return function (...args) {
				const currentTime = Date.now();
				if (currentTime - lastExecTime > delay) {
					func.apply(this, args);
					lastExecTime = currentTime;
				} else {
					clearTimeout(timeoutId);
					timeoutId = setTimeout(() => {
						func.apply(this, args);
						lastExecTime = Date.now();
					}, delay - (currentTime - lastExecTime));
				}
			};
		},
		
		// 处理头像加载错误
		handleAvatarError(e) {
			e.target.src = '/img/default-avatar.png';
		},
		
		// LLM流式消息处理方法
		handleLLMStreamRequest(data) {
			// 准备流式传输，不创建UI
			// 分析问题内容
			
			// 尝试从问题中提取任务名（用于续问功能）
			let taskInfo = null;
			if (data.question) {
				// 查找是否包含@任务[任务名]格式
				const taskMatch = data.question.match(/@任务\[([^\]]+)\]/);
				// 任务匹配结果
				
				if (taskMatch) {
					const taskName = taskMatch[1].trim();
					// 如果不是"新建任务"，则提取为任务信息
					if (taskName !== '新建任务') {
						taskInfo = {
							name: taskName,
							id: data.streamId // 使用streamId作为临时id
						};
						// 从LLM_STREAM_REQUEST提取任务信息
					} else {
						// 跳过"新建任务"的任务信息提取
					}
				} else {
					// 未匹配到任务格式
				}
			}
			
			// 仅创建流信息用于准备，不设置activeLLMStream（避免UI立即创建）
			const streamInfo = {
				streamId: data.streamId,
				title: data.question || 'AI 助手',
				startTime: Date.now(),
				taskInfo: taskInfo, // 缓存任务信息
				isStarted: false // 标记尚未开始
			};
			
			// 仅存储流信息，不设置为活跃流
			this.llmStreams.set(data.streamId, streamInfo);
			// 流式传输已准备，等待第一个chunk创建UI
		},
		
		handleLLMStreamChunk(data) {
			console.log('[ChatBox] handleLLMStreamChunk received:', { streamId: data.streamId, chunk: data.chunk?.substring(0, 50) });

			// 智能体 iframe 模式下忽略全局 LLM 流
			if (this.currentAgentChat) {
				console.log('[ChatBox] 当前为智能体对话，忽略全局LLM流');
				return;
			}

			// 🔥 检测是否为 A2AClient 的结构化消息（会由 handleAgentMessage 处理）
			let structuredChunk = null;
			try {
				const parsed = JSON.parse(data.chunk);
				if (parsed && parsed.type) {
					structuredChunk = parsed;
					console.log('[ChatBox] 检测到结构化消息，跳过普通处理:', parsed.type);
					// 🔥 结构化消息由A2AClient的handleAgentMessage处理，这里直接返回
					return;
				}
			} catch {
				// 不是 JSON 或没有 type 字段，继续处理
			}

			// 获取流信息
			let streamInfo = this.llmStreams.get(data.streamId);
			if (!streamInfo) {
				// 如果没有streamInfo，可能是智能体直接发送chunk（没有先发送REQUEST）
				// 动态创建streamInfo
				console.log('[ChatBox] 没有找到streamInfo，动态创建 for streamId:', data.streamId);
				streamInfo = {
					streamId: data.streamId,
					title: 'AI 助手',
					startTime: Date.now(),
					taskInfo: null,
					isStarted: false
				};
				this.llmStreams.set(data.streamId, streamInfo);
			}

			// 如果是第一个chunk，创建activeLLMStream并触发UI创建
			if (!streamInfo.isStarted) {
				// 第一个chunk到达，开始创建LLM流式消息UI
				console.log('[ChatBox] 第一个chunk，创建UI:', data.streamId);
				streamInfo.isStarted = true;
				this.activeLLMStream = streamInfo;

				// 🔥 检测消息类型：判断是否为 RooCode agent 的结构化消息
					if (structuredChunk) {
						streamInfo.messageType = 'roocode-agent';
						console.log('[ChatBox] ✅ 检测到 RooCode Agent 消息流, type:', structuredChunk.type);
					} else {
						// 普通文本消息
						streamInfo.messageType = 'text';
						console.log('[ChatBox] 检测到普通文本消息流');
					}

				// 等待组件渲染并初始化
				this.$nextTick(() => {
					requestAnimationFrame(() => {
						const streamRef = this.$refs[`llm-stream-${data.streamId}`];
						if (streamRef) {
							const component = Array.isArray(streamRef) ? streamRef[0] : streamRef;
							if (component && component.startStream) {
								component.startStream();
							}
						}
					});
				});
			}

			// 检查是否是当前活跃的流
			if (this.activeLLMStream && this.activeLLMStream.streamId === data.streamId) {
				// 添加文本块到流式消息组件
				const streamRef = this.$refs[`llm-stream-${data.streamId}`];
				if (streamRef) {
					const component = Array.isArray(streamRef) ? streamRef[0] : streamRef;
					if (component && component.addChunk) {
						console.log('[ChatBox] Calling addChunk on component');
						component.addChunk(structuredChunk || data.chunk, data.sequence);
					} else {
						console.error('[ChatBox] ❌ Component or addChunk method not found');
					}
				} else {
					console.error('[ChatBox] ❌ Stream ref not found:', `llm-stream-${data.streamId}`);
				}

				// 立即滚动到底部
				this.$nextTick(() => {
					this.scrollToBottom();
				});
			}
		},
		
		handleLLMStreamEnd(data) {
				// LLM流式传输结束
			// 检查任务信息
			
			// 检查是否是当前活跃的流
			if (this.activeLLMStream && this.activeLLMStream.streamId === data.streamId) {
				// 流式结束，获取真实任务信息
				
				// 从拆分的字段中重构任务信息
				let taskInfo = null;
				if (data.taskName) {
					taskInfo = {
						name: data.taskName,  // 这里的taskName已经包含零宽编码
						id: data.taskId
					};
					// 从拆分字段重构任务信息
				} else {
					// 后端未提供taskName
				}
				
				// 🔧 检查是否在智能体对话模式下，智能体对话不需要任务续问
				const isAgentChat = !!this.currentAgentChat;

				// 如果有有效的任务信息，且不在智能体对话模式，设置续问
				if (taskInfo && taskInfo.name && taskInfo.name !== '新建任务' && !isAgentChat) {
					this.lastCompletedTask = taskInfo;
					// 设置任务续问信息
					
					// 通知 ChatInput 设置任务续问
					this.$nextTick(() => {
						this.setTaskContinuation(this.lastCompletedTask);
					});
				} else {
					// 没有有效任务信息或在智能体对话模式
				}
				
				// 结束流式传输
				const streamRef = this.$refs[`llm-stream-${data.streamId}`];
				if (streamRef) {
					const component = Array.isArray(streamRef) ? streamRef[0] : streamRef;
					if (component && component.endStream) {
						component.endStream();
					}
				}
			}
		},
		
		// 无缝任务续问功能
		setTaskContinuation(taskInfo) {
			// 设置任务续问
			// 获取 ChatInput 组件引用
			const chatInputRef = this.$refs.chatInput;
			if (chatInputRef && chatInputRef.setTaskContinuation) {
				chatInputRef.setTaskContinuation(taskInfo);
			}
		},
		
		handleLLMStreamError(data) {
			console.error('[ChatBox] LLM流式传输错误:', data);
			
			// 检查是否是当前活跃的流
			if (this.activeLLMStream && this.activeLLMStream.streamId === data.streamId) {
				// 处理错误
				const streamRef = this.$refs[`llm-stream-${data.streamId}`];
				if (streamRef) {
					const component = Array.isArray(streamRef) ? streamRef[0] : streamRef;
					if (component && component.handleError) {
						component.handleError(data.error || '流式传输错误');
					}
				}
			}
		},
		
		onLLMStreamCompleted(result) {
			
			// 保存当前滚动位置
			const scrollBox = document.getElementById('chatScrollBox');
			const currentScroll = scrollBox ? scrollBox.scrollTop : 0;
			
			// 创建与流式消息完全相同的最终消息
			const msgInfo = {
				tmpId: result.streamId, // 使用相同的ID，避免key变化导致的重新渲染
				sendId: 0, // 系统消息
				sendNickName: 'AI 助手',
				sendTime: this.activeLLMStream ? this.activeLLMStream.startTime : new Date().getTime(),
				type: this.$enums.MESSAGE_TYPE.TEXT,
				content: result.content,
				selfSend: false
			};

			// 根据会话类型设置接收者
			if (this.chat.type === "PRIVATE") {
				msgInfo.recvId = this.mine.id;
			} else {
				msgInfo.groupId = this.group.id;
			}

			// 先清除活跃流（移除流式消息）
			this.activeLLMStream = null;
			this.llmStreams.delete(result.streamId);
			
			// 使用 nextTick 确保 DOM 更新完成后再插入新消息
			this.$nextTick(() => {
				// 添加到消息列表（替换流式消息）
				this.$store.commit("insertMessage", [msgInfo, this.chat]);
				
				// 恢复滚动位置，避免跳动
				this.$nextTick(() => {
					if (scrollBox) {
						scrollBox.scrollTop = currentScroll;
					}
				});
			});
		},
		
		onLLMStreamError(error) {
			// console.error('[ChatBox] LLM流式消息错误:', error);
			
			// 显示错误提示
			this.$message.error(`AI响应失败: ${error.error}`);
			
			// 调用错误处理方法
			if (this.activeLLMStream) {
				this.handleLLMStreamError({ streamId: this.activeLLMStream.streamId, error: error.error });
			}
		},

		registerLLMStreamHandlers() {
			// 注册WebSocket消息回调
			const originalCallback = this.$wsApi.getMessageCallBack();
			
			// 设置新的消息回调，包装原有的回调
			this.$wsApi.setMessageCallBack((cmd, data) => {
				// 处理LLM流式消息
				switch(cmd) {
					case 10: // LLM_STREAM_REQUEST
						this.handleLLMStreamRequest(data);
						break;
					case 11: // LLM_STREAM_CHUNK
						this.handleLLMStreamChunk(data);
						break;
					case 12: // LLM_STREAM_END
						this.handleLLMStreamEnd(data);
						break;
					case 13: // LLM_STREAM_ERROR
						this.handleLLMStreamError(data);
						break;
					// 🔥 智能体任务消息 (60-69)
					case 60: // 智能体任务进度
					case 61: // 智能体任务结果
					case 62: // 智能体任务错误
						this.handleAgentTaskMessage(cmd, data);
						break;
				}

				// 调用原有的回调
				if (originalCallback) {
					originalCallback(cmd, data);
				}
			});
		},

		getCurrentTerminal() {
			// 根据环境判断当前终端类型
			if (typeof window !== 'undefined') {
				// 检测VSCode环境 - 多种方式检测
				if (window.parent !== window || // 在iframe中
					window.acquireVsCodeApi || // VSCode WebView API
					window.SharedServicesAccessor || // 我们的VSCode服务
					navigator.userAgent.includes('VSCode') || // User Agent
					window.location.protocol === 'vscode-webview:') { // VSCode WebView协议
					return 2; // VSCode
				}
				
				// 检测Electron环境
				if (window.require || 
					window.process?.type || 
					window.isElectron ||
					navigator.userAgent.includes('Electron')) {
					return 3; // PC客户端
				}
				
				// 检测移动端App环境
				if (window.cordova || 
					window.PhoneGap || 
					window.phonegap ||
					navigator.userAgent.includes('wv') || // WebView
					navigator.userAgent.includes('Mobile')) {
					return 1; // App
				}
				
				return 0; // Web
			}
			return 0; // 默认Web
		},
		
		isMineMessage(msgInfo) {
			// 如果有selfSend字段，直接使用
			if (msgInfo.selfSend !== undefined) {
				return msgInfo.selfSend;
			}
			
			// 首先判断是否为同一用户，修复在拖拽发送生成的消息中sendId为"123"的问题
			if (parseInt(msgInfo.sendId) !== parseInt(this.mine.id)) {
				return false;
			}
			
			// 对于终端会话，需要额外判断终端类型
			if (this.chat.isTerminalChat || this.chat.isTerminalInbox) {
				const currentTerminal = this.getCurrentTerminal();
				// 如果消息有senderTerminal字段，比较终端类型
				if (msgInfo.senderTerminal !== undefined) {
					return msgInfo.senderTerminal === currentTerminal;
				}
			}
			
			// 普通会话或没有终端信息的消息，只判断用户ID
			return true;
		},
		
		// ===== 智能体相关方法 =====

		resolveParentChat() {
			if (this.currentAgentChat?.parentChat) {
				return this.currentAgentChat.parentChat;
			}
			if (this.chat.type !== 'AGENT') {
				return this.chat;
			}
			return this.findParentChatByTargetId(this.chat.targetId);
		},

		findParentChatByTargetId(targetId) {
			if (!targetId || !targetId.includes('_agent_')) {
				return null;
			}
			const parts = targetId.split('_agent_');
			if (parts.length !== 2) {
				return null;
			}
			const parentPart = parts[0];
			const lastUnderscore = parentPart.lastIndexOf('_');
			if (lastUnderscore === -1) {
				return null;
			}
			const parentType = parentPart.substring(0, lastUnderscore);
			const parentTargetId = parentPart.substring(lastUnderscore + 1);
			const chats = this.$store.getters.findChats();
			return chats.find(c =>
				c.type === parentType &&
				String(c.targetId) === String(parentTargetId)
			) || null;
		},

		enterAgentChatContext(agentContext) {
			if (!agentContext) {
				this.exitAgentChat();
				return;
			}
			const context = {
				...agentContext,
				showName: agentContext.showName || agentContext.agentName
			};
			this.currentAgentChat = context;
			// 注意：showAgentIframe 和 iframeLoading 由调用方控制
			// 这样可以确保在 VNC 同步期间一直显示 loading
			this.$nextTick(() => {
				this.syncAgentInfoToIframe();
				this.syncImSessionInfoToIframe();
				this.scrollToBottom();
			});
		},

		activateAgentChatFromChat(chat) {
			if (!chat) {
				return;
			}
			const parentChat = this.findParentChatByTargetId(chat.targetId);
			const agentContext = {
				agentId: chat.agentId || chat.agent?.id || chat.targetId,
				agentName: chat.agentName || chat.agent?.name || chat.showName,
				agent: chat.agent,
				parentChat,
				targetId: chat.targetId,
				showName: chat.showName,
				headImage: chat.headImage,
				contextType: 'chat'
			};
			this.enterAgentChatContext(agentContext);

			// 建立 IM chatId 和 Happy sessionId 的映射
			// 使用 targetId 作为 sessionId（临时方案，后续可以从 iframe 响应中获取真实的 sessionId）
			this.$store.commit('setHappySessionMapping', {
				imChatId: chat.targetId,
				happySessionId: chat.targetId // 临时使用相同的 ID
			});
		},

		/**
		 * 执行自动启动流程
		 * @param {string|number} agentId 智能体ID
		 */
		async performAutoStart(agentId) {
			console.log('[ChatBox] performAutoStart 开始执行，agentId:', agentId);

			try {
				// 获取智能体信息
				const agent = await this.fetchAgentInfo(agentId);

				if (!agent) {
					console.error('[ChatBox] 获取智能体信息失败');
					this.$message.error('未找到该智能体');
					return;
				}

				console.log('[ChatBox] 智能体信息获取成功:', agent.agentName);

				// 确保正确的IM父会话已激活
				const parentChat = await this.ensureParentChatActivated(agent);

				if (!parentChat) {
					console.error('[ChatBox] 无法确定父会话');
					this.$message.error('无法创建智能体会话，请稍后重试');
					return;
				}

				console.log('[ChatBox] 父会话已激活:', parentChat.showName);

				// 调用完整的初始化流程
				await this.startAgentChat({
					agent,
					member: this.mine
				});

				console.log('[ChatBox] 智能体会话启动成功');
			} catch (error) {
				console.error('[ChatBox] 智能体自动启动失败:', error);
				this.$message.error('智能体初始化失败,请稍后重试');
			} finally {
				// 清除URL参数,避免重复触发
				const currentQuery = this.$route.query;
				if (currentQuery.autoStart === 'true') {
					console.log('[ChatBox] 清除 URL 参数');
					this.$router.replace({
						path: this.$route.path,
						query: {
							...currentQuery,
							autoStart: undefined,
							agentId: undefined
						}
					});
				}
			}
		},

		async fetchAgentInfo(agentId) {
			try {
				// httpRequest.js 的响应拦截器已经提取了 response.data.data
				// 所以这里直接返回 response 即可
				const agent = await this.$http.get(`/agent/${agentId}`);
				console.log('[ChatBox] fetchAgentInfo 获取到智能体信息:', agent);
				return agent;
			} catch (error) {
				console.error('获取智能体信息失败:', error);
				throw new Error('获取智能体信息失败');
			}
		},

		/**
		 * 确保正确的IM父会话已激活
		 * 根据智能体的访问权限类型，确定并激活对应的IM会话
		 */
		async ensureParentChatActivated(agent) {
			const accessScope = agent.publishSettings?.accessScope || 'PUBLIC';
			let parentChat = null;

			if (accessScope === 'FRIENDS') {
				// FRIENDS类型：使用与智能体创建者的私聊会话
				const agentOwnerId = agent.userId;

				// 查找已存在的私聊会话
				const chats = this.$store.getters.findChats();
				parentChat = chats.find(c =>
					c.type === 'PRIVATE' &&
					String(c.targetId) === String(agentOwnerId)
				);

				if (!parentChat) {
					// 创建新的私聊会话
					try {
						const userInfo = await this.$http.get(`/user/find/${agentOwnerId}`);

						const newChat = {
							type: 'PRIVATE',
							targetId: agentOwnerId,
							showName: userInfo.data.nickName || '未知用户',
							headImage: userInfo.data.headImageThumb || userInfo.data.headImage || '',
							messages: [],
							lastContent: '',
							lastSendTime: Date.now(),
							unreadCount: 0,
							stored: false
						};

						this.$store.commit('openChat', newChat);
						// 重新查找会话实例
						parentChat = this.$store.state.chatStore.chats.find(c =>
							c.type === 'PRIVATE' && String(c.targetId) === String(agentOwnerId)
						);
					} catch (error) {
						console.error('[ChatBox] 创建私聊会话失败:', error);
						throw new Error('无法创建与智能体创建者的会话');
					}
				}

			} else if (accessScope === 'GROUP_MEMBERS') {
				// GROUP_MEMBERS类型：使用智能体配置的群组会话
				const groupSettings = agent.publishSettings?.groupSettings;
				const selectedGroups = groupSettings?.selectedGroups || [];

				if (selectedGroups.length === 0) {
					throw new Error('智能体未配置访问群组');
				}

				// 使用第一个群组或默认群组
				const targetGroupId = groupSettings.defaultJoinGroup || selectedGroups[0];

				// 查找已存在的群组会话
				const chats = this.$store.getters.findChats();
				parentChat = chats.find(c =>
					c.type === 'GROUP' &&
					String(c.targetId) === String(targetGroupId)
				);

				if (!parentChat) {
					// 创建新的群组会话
					try {
						const groupInfo = await this.$http.get(`/group/find/${targetGroupId}`);

						const newChat = {
							type: 'GROUP',
							targetId: targetGroupId,
							showName: groupInfo.data.showGroupName || groupInfo.data.name,
							headImage: groupInfo.data.headImageThumb || groupInfo.data.headImage || '',
							messages: [],
							lastContent: '',
							lastSendTime: Date.now(),
							unreadCount: 0,
							stored: false
						};

						this.$store.commit('openChat', newChat);
						// 重新查找会话实例
						parentChat = this.$store.state.chatStore.chats.find(c =>
							c.type === 'GROUP' && String(c.targetId) === String(targetGroupId)
						);
					} catch (error) {
						console.error('[ChatBox] 创建群组会话失败:', error);
						throw new Error('无法创建群组会话');
					}
				}

			} else {
				// PUBLIC类型：使用当前用户自己的私聊会话
				const myId = this.mine.id;

				// 查找已存在的自己的会话
				const chats = this.$store.getters.findChats();
				parentChat = chats.find(c =>
					c.type === 'PRIVATE' &&
					String(c.targetId) === String(myId)
				);

				if (!parentChat) {
					// 创建自己的私聊会话
					const newChat = {
						type: 'PRIVATE',
						targetId: myId,
						showName: this.mine.nickName || '我',
						headImage: this.mine.headImageThumb || this.mine.headImage || '',
						messages: [],
						lastContent: '',
						lastSendTime: Date.now(),
						unreadCount: 0,
						stored: false
					};

					this.$store.commit('openChat', newChat);
					// 重新查找会话实例
					parentChat = this.$store.state.chatStore.chats.find(c =>
						c.type === 'PRIVATE' && String(c.targetId) === String(myId)
					);
				}
			}

			// 激活父会话
			if (parentChat) {
				this.$store.commit('activeChat', parentChat);
				await this.$nextTick();
				return parentChat;
			}

			return null;
		},

		/**
		 * ==========================================
		 * 🎯 智能体会话初始化相关方法
		 * ==========================================
		 */

		/**
		 * 等待用户信息就绪（基于 Promise + Watcher 机制）
		 * @returns {Promise<void>} 就绪时 resolve，超时时 reject
		 */
		waitForMineReady() {
			console.log('[agent share debug] 检查用户信息就绪状态, mine:', this.mine);

			// 如果已就绪，立即返回
			if (this.mine && this.mine.id) {
				console.log('[agent share debug] ✅ 用户信息已就绪, mine.id:', this.mine.id);
				return Promise.resolve();
			}

			console.log('[agent share debug] ⏳ 用户信息未就绪，等待中...');

			// 创建 Promise，通过 watcher 触发
			return new Promise((resolve, reject) => {
				// 设置超时保护（10秒）
				const timeoutId = setTimeout(() => {
					cleanup();
					console.error('[agent share debug] ❌ 用户信息加载超时');
					reject(new Error('用户信息加载超时，请刷新页面重试'));
				}, 10000);

				// 创建 watcher 监听 mine 变化
				const unwatch = this.$watch('mine', (newMine) => {
					console.log('[agent share debug] 📢 mine 变化:', newMine);

					if (newMine && newMine.id) {
						console.log('[agent share debug] ✅ 用户信息就绪, mine.id:', newMine.id);
						cleanup();
						resolve();
					}
				}, { immediate: true, deep: true });

				// 清理函数
				function cleanup() {
					clearTimeout(timeoutId);
					unwatch();
				}
			});
		},

		/**
		 * 更新初始化步骤状态
		 * @param {string} stepKey - 步骤key
		 * @param {string} status - 状态: pending/loading/success/error
		 * @param {string|null} error - 错误信息
		 */
		updateStepStatus(stepKey, status, error = null) {
			const stepIndex = this.agentInitProgress.steps.findIndex(s => s.key === stepKey);
			if (stepIndex !== -1) {
				this.agentInitProgress.steps[stepIndex].status = status;
				this.agentInitProgress.steps[stepIndex].error = error;
				this.agentInitProgress.currentStep = stepIndex;

				console.log(`[agent share debug] 📊 步骤更新: ${stepKey} -> ${status}`, error || '');
			}
		},

		/**
		 * 重置初始化进度
		 */
		resetInitProgress() {
			console.log('[agent share debug] 🔄 重置初始化进度');
			this.agentInitProgress.steps.forEach(step => {
				step.status = 'pending';
				step.error = null;
			});
			this.agentInitProgress.currentStep = 0;
		},

		/**
		 * 处理初始化重试
		 */
		async handleInitRetry() {
			console.log('[agent share debug] 🔄 用户点击重试');

			// 从当前错误的步骤开始重新初始化
			const failedStepIndex = this.agentInitProgress.steps.findIndex(s => s.status === 'error');
			if (failedStepIndex !== -1) {
				const failedStepKey = this.agentInitProgress.steps[failedStepIndex].key;
				console.log('[agent share debug] 从失败的步骤重试:', failedStepKey);

				// 重置后续步骤
				for (let i = failedStepIndex; i < this.agentInitProgress.steps.length; i++) {
					this.agentInitProgress.steps[i].status = 'pending';
					this.agentInitProgress.steps[i].error = null;
				}

				// 重新获取agentId（从URL或其他地方）
				const agentId = this.$route.query.agentId;
				if (agentId) {
					// 从sessionStorage读取智能体数据
					const pendingAgentData = sessionStorage.getItem('pendingAgentData');
					let agentData = null;
					if (pendingAgentData) {
						try {
							agentData = JSON.parse(pendingAgentData);
						} catch (e) {
							console.warn('[agent share debug] 解析 pendingAgentData 失败', e);
						}
					}

					await this.initAgentSession(agentId, {
						agentData,
						fromShare: true
					});
				}
			}
		},

		/**
		 * 处理初始化取消
		 */
		handleInitCancel() {
			console.log('[agent share debug] ❌ 用户点击取消');

			// 关闭初始化面板
			this.agentInitProgress.show = false;
			this.showAgentIframe = false;
			this.iframeLoading = false;

			// 重置进度
			this.resetInitProgress();
		},

		handleInitClose() {
			// 关闭初始化面板
			this.agentInitProgress.show = false;
			this.iframeLoading = false;

			// 重置进度
			this.resetInitProgress();

			// 激活智能体会话
			if (this.currentAgentChat && this.currentAgentChat.targetId) {
				const agentChatIndex = this.$store.state.chatStore.chats.findIndex(
					c => c.type === 'AGENT' && c.targetId === this.currentAgentChat.targetId
				);
				if (agentChatIndex !== -1) {
					this.$store.commit('activeChat', agentChatIndex);
				}
			}
		},
		/**
		 * 统一的智能体会话初始化入口
		 * @param {string} agentId - 智能体ID
		 * @param {object} options - 可选配置
		 * @param {boolean} options.fromShare - 是否来自分享链接
		 */
		async initAgentSession(agentId, options = {}) {
			// 显示初始化面板
			this.showAgentIframe = true;
			this.agentInitProgress.show = true;
			this.resetInitProgress();

			// 等待 UI 渲染
			await this.ensureUIRendered();

			try {
				// 步骤 1: 等待用户信息就绪
				this.updateStepStatus('userInfo', 'loading');
				await this.waitForMineReady();
				this.updateStepStatus('userInfo', 'success');

				// 步骤 2: 获取智能体信息
				this.updateStepStatus('agentInfo', 'loading');
				const agent = await this.fetchAgentInfo(agentId);
				if (!agent) {
					throw new Error('未找到智能体信息');
				}
				this.updateStepStatus('agentInfo', 'success');

				// 步骤 3: VNC Pod 同步
				this.updateStepStatus('vncPod', 'loading');
				await this.syncVncChatsOnAgentStart();
				this.updateStepStatus('vncPod', 'success');

				// 步骤 4: 会话环境初始化
				this.updateStepStatus('session', 'loading');

				// 🔥 从 chatStore 中查找已有的智能体会话（后端已创建）
				const agentChat = this.$store.state.chatStore.chats.find(c =>
					c.type === 'AGENT' && c.agentId === agentId
				);

				if (!agentChat) {
					throw new Error('未找到智能体会话，请确保已调用 /agent/session/ensure');
				}

				console.log('[ChatBox] 找到智能体会话:', agentChat);

				// 从智能体会话中获取父会话信息
				const parentType = agentChat.parentType;
				const parentId = agentChat.parentId;
				const parentName = agentChat.parentName;

				if (!parentType || !parentId) {
					throw new Error('智能体会话缺少父会话信息');
				}

				// 查找或创建父会话
				let parentChat = this.$store.state.chatStore.chats.find(c =>
					c.type === parentType && String(c.targetId) === String(parentId)
				);

				if (!parentChat) {
					console.log('[ChatBox] 创建父会话，使用智能体会话中的 parentName');
					// 使用智能体会话中的信息创建父会话
					const newChat = {
						type: parentType,
						targetId: parentId,
						showName: parentName || '未知会话',
						headImage: agentChat.headImage || '',
						messages: [],
						lastContent: '',
						lastSendTime: Date.now(),
						unreadCount: 0,
						stored: false
					};

					this.$store.commit('openChat', newChat);

					// 重新查找
					parentChat = this.$store.state.chatStore.chats.find(c =>
						c.type === parentType && String(c.targetId) === String(parentId)
					);

					if (!parentChat) {
						throw new Error('创建父会话失败');
					}
				}

				console.log('[ChatBox] 父会话已就绪:', parentChat.showName);

				// 构建智能体上下文
				const agentContext = {
					agentId: agent.agentId,
					agentName: agent.agentName,
					agent,
					parentChat,
					targetId: agentChat.targetId, // 使用智能体会话的 targetId
					showName: agentChat.showName || `${parentChat.showName} › ${agent.agentName}`,
					headImage: agent.avatarUrl || agent.headImage || parentChat.headImage,
					contextType: 'embedded'
				};

				// 进入智能体对话上下文
				this.enterAgentChatContext(agentContext);
				this.updateStepStatus('session', 'success');

				// 步骤 5: 完成
				this.updateStepStatus('complete', 'success');

				// 🔥 移动端全屏模式：自动关闭初始化面板
				if (this.isMobile) {
					console.log('[ChatBox] 移动端全屏模式 - 自动关闭初始化面板');
					setTimeout(() => {
						this.handleInitClose();
					}, 1000); // 延迟1秒，让用户看到完成状态
				}

				// 创建智能体会话（不激活，避免组件重新创建）
				this.$nextTick(() => {
					this.$store.commit('ensureAgentChat', {
						parentChat,
						agent,
						ownerId: this.mine.id,
						setActive: false
					});
				});

			} catch (error) {
				console.error('智能体初始化失败:', error);

				// 更新当前步骤为错误状态
				const currentStepKey = this.agentInitProgress.steps[this.agentInitProgress.currentStep].key;
				this.updateStepStatus(currentStepKey, 'error', error.message || '未知错误');
			}
		},

		async startAgentChat(payload) {
			const { agent } = payload;
			if (!agent) {
				this.$message.error('未找到智能体');
				return;
			}

			// 🔒 检查智能体访问权限（按钮已禁用，这里是二次防护，不重复提示）
			if (agent.canAccess === false) {
				console.warn('[ChatBox] 智能体无访问权限:', agent.accessDeniedReason);
				return;
			}

			try {
				// 🔥 步骤1: 调用后端接口创建智能体会话
				console.log('[ChatBox] 调用 /agent/session/ensure 创建会话, agentId:', agent.agentId);
				const res = await this.$http.post('/agent/session/ensure', {
					agentId: agent.agentId
				});
				console.log('[ChatBox] /agent/session/ensure 调用成功');


				// 检查是否需要等待审批
				const sessionData = res;
				if (sessionData && (sessionData.status === 'PENDING_FRIEND' || sessionData.status === 'PENDING_GROUP')) {
					this.$message.info(sessionData.pendingMessage || '已发送申请，等待验证后即可使用');
					return;
				}
				// 🔥 步骤2: 刷新智能体会话列表
				console.log('[ChatBox] 刷新智能体会话列表...');
				await this.$store.dispatch('loadAgentChatsFromServer');
				console.log('[ChatBox] 智能体会话列表刷新完成');

				// 🔥 步骤3: 等待会话出现在 chatStore 中
				console.log('[ChatBox] 等待智能体会话出现在 chatStore...');
				let retries = 20; // 最多等待10秒
				let agentChat = null;

				while (retries > 0) {
					agentChat = this.$store.state.chatStore.chats.find(c =>
						c.type === 'AGENT' && c.agentId === agent.agentId
					);

					if (agentChat) {
						console.log('[ChatBox] 找到智能体会话:', agentChat);
						break;
					}

					await new Promise(resolve => setTimeout(resolve, 500));
					retries--;
				}

				if (!agentChat) {
					throw new Error('创建智能体会话超时，请重试');
				}

				// 🔥 步骤4: 激活智能体会话
				const agentChatIndex = this.$store.state.chatStore.chats.findIndex(c =>
					c.type === 'AGENT' && c.agentId === agent.agentId
				);

				if (agentChatIndex === -1) {
					throw new Error('无法找到智能体会话索引');
				}

				console.log('[ChatBox] 激活智能体会话, index:', agentChatIndex);
				this.$store.commit('activeChat', agentChatIndex);

				// 🔥 步骤5: 初始化智能体会话UI
				await this.initAgentSession(agent.agentId, {
					fromShare: false
				});

				this.hideAgentSidePanel();
			} catch (error) {
				console.error('[ChatBox] startAgentChat 失败:', error);
				// 处理权限错误
				const errorMsg = error.response?.data?.message || error.message || '启动智能体会话失败';
				if (errorMsg.includes('好友') || errorMsg.includes('群组')) {
					this.$message.warning(errorMsg);
				} else {
					this.$message.error(errorMsg);
				}
			}
		},

		/**
		 * 🔧 确保 UI 真正渲染到屏幕
		 * 等待 Vue 更新 + 浏览器完成绘制
		 */
		async ensureUIRendered() {
			await this.$nextTick();
			await new Promise(resolve => {
				requestAnimationFrame(() => {
					requestAnimationFrame(resolve);
				});
			});
		},

		/**
		 * 激活智能体对话时同步用户聊天到VNC Pod
		 */
		async syncVncChatsOnAgentStart() {
			console.log('[ChatBox VNC Sync] 🚀 方法被调用');
			console.log('[ChatBox VNC Sync] 📊 当前面板显示状态: agentInitProgress.show =', this.agentInitProgress.show);
			try {
				const userId = this.$store.state.userStore.userInfo?.id;
				console.log('[ChatBox VNC Sync] 用户ID:', userId);

				if (!userId || userId === -1) {
					console.log('[ChatBox VNC Sync] ⚠️ 用户ID无效，跳过同步');
					return;
				}

				console.log('[ChatBox VNC Sync] 激活智能体对话，检查VNC Pod状态...');

				// 1. 确保VNC Pod已创建并运行（不传递回调，所有状态通过初始化面板显示）
				console.log('[ChatBox VNC Sync] 📡 准备调用 ensurePodReady，此时面板应该已经显示');
				await ensurePodReady();
				console.log('[ChatBox VNC Sync] ✅ ensurePodReady 完成');

				console.log('[ChatBox VNC Sync] ✅ VNC Pod已就绪，开始同步聊天记录...');

				// 2. 同步用户聊天记录到VNC Pod
				const result = await autoSyncUserChats(userId);

				if (result && result.success) {
					const totalSynced = (result.private_chats_synced || 0) + (result.groups_synced || 0);
					console.log(`[ChatBox VNC Sync] ✅ 已同步 ${totalSynced} 个聊天`);
				}
			} catch (error) {
				console.error('[ChatBox VNC Sync] ❌ 同步失败:', error);
				// 🔥 关键修复：必须抛出错误，让上层的 try-catch 捕获
				// 这样才能正确隐藏 iframe 和 loading，避免显示无法使用的界面
				throw new Error(`云电脑初始化失败: ${error.message || '未知错误'}`);
			}
		},

		/**
		 * 🔥 处理任务已调度事件（任务接口成功返回后）
		 */
		handleTaskDispatched(taskInfo) {
			console.log('[ChatBox] 🔥 任务已调度:', taskInfo);

			// 更新状态卡片为"已调度"，并保存 sessionId
			this.$store.commit('updateAgentTaskStatus', {
				taskId: taskInfo.taskId,
				newStatus: 'running',
				message: '任务正在执行中',
				extraData: {
					// 🔥 保存 sessionId，用于后续 WebSocket 消息定位
					sessionId: taskInfo.happySessionId || taskInfo.result?.sessionId
				}
			});
		},

		/**
		 * 🔥 处理智能体任务 WebSocket 消息
		 * @param {number} cmd - 消息类型 (60: 进度, 61: 结果, 62: 错误)
		 * @param {Object} data - 消息数据
		 */
		handleAgentTaskMessage(cmd, data) {
			console.log('[ChatBox] 🔥 收到智能体任务消息:', cmd, data);

			if (!data) {
				console.warn('[ChatBox] 智能体任务消息数据为空');
				return;
			}

			// 🔥 支持通过 taskId 或 sessionId 定位任务
			let taskId = data.taskId;

			// 如果没有 taskId，尝试通过 sessionId 查找
			if (!taskId && data.sessionId) {
				const mappings = this.$store.state.chatStore.agentTaskSessionMappings;
				taskId = mappings[data.sessionId];
				console.log('[ChatBox] 通过 sessionId 查找 taskId:', data.sessionId, '->', taskId);
			}

			if (!taskId) {
				console.warn('[ChatBox] 无法定位任务，缺少 taskId 和 sessionId');
				return;
			}

			// 🔥 构造AGENT_TASK_STATUS消息（作为新消息插入）
			const { updateTaskStatus } = require('@/api/messageStructures');

			// 获取当前最新状态（从索引中）
			const currentStatus = this.$store.state.chatStore.agentTaskStatuses[taskId];

			let newStatus, statusMessage, extraData = {};

			// 根据消息类型确定新状态
			switch (cmd) {
				case 60: // 进度消息
					newStatus = data.status || 'running';
					statusMessage = data.message || `任务进度: ${data.progress}%`;
					if (data.confirmationPanel) {
						extraData.confirmationPanel = data.confirmationPanel;
					}
					if (data.sessionId) {
						extraData.sessionId = data.sessionId;
					}
					break;

				case 61: // 结果消息
					newStatus = 'completed';
					statusMessage = data.message || '任务完成';
					extraData.result = data.result || { status: 'success', message: data.message };
					if (data.sessionId) {
						extraData.sessionId = data.sessionId;
					}
					break;

				case 62: // 错误消息
					newStatus = 'error';
					statusMessage = data.message || '任务执行失败';
					extraData.result = { status: 'error', message: data.message };
					break;
			}

			// 更新状态数据（使用现有的或从AGENT_MENTION消息中提取）
			const updatedStatusContent = updateTaskStatus(
				currentStatus || {
					type: 'AGENT_TASK_STATUS',
					version: '1.0',
					taskId: taskId,
					agentId: data.agentId,
					agentName: data.agentName,
					status: 'initializing',
					statusHistory: [],
					createTime: Date.now()
				},
				newStatus,
				statusMessage,
				extraData
			);

			// 🔥 创建AGENT_TASK_STATUS消息并插入到消息列表
			const statusMsg = {
				tmpId: `status-${taskId}-${Date.now()}`,
				type: 0, // TEXT类型
				content: JSON.stringify(updatedStatusContent),
				sendId: 0, // 系统消息
				sendTime: Date.now(),
				selfSend: false,
				status: 0
			};

			// 根据会话类型填充接收者ID
			if (this.chat.type === "PRIVATE") {
				statusMsg.recvId = this.$store.state.userStore.userInfo?.id;
			} else {
				statusMsg.groupId = this.chat.targetId;
			}

			// 插入消息（AGENT_TASK_STATUS消息会被UI过滤，不显示）
			this.$store.commit("insertMessage", [statusMsg, this.chat]);

			// 更新索引
			this.$store.commit('updateAgentTaskStatusIndex', {
				taskId,
				statusContent: updatedStatusContent,
				chatId: this.chat.targetId
			});

			console.log('[ChatBox] 智能体任务状态已更新并插入消息:', taskId, newStatus);
		},

		/**
		 * 🎯 处理智能体分享链接的会话创建
		 * 从Chat.vue接收事件，创建智能体会话
		 */
		async handleStartAgentFromShare(payload) {
			console.log('[ChatBox] 🎯 handleStartAgentFromShare 被调用，payload:', payload);
			const { agent, parentChat } = payload;

			if (!agent || !parentChat) {
				console.error('[ChatBox] ⚠️ agent或parentChat为空，payload:', payload);
				this.$message.error('智能体信息不完整');
				return;
			}

			try {
				// 1. 切换到父级会话
				console.log('[ChatBox] 切换到父级会话:', parentChat);
				const parentChatObj = this.$store.getters.findChats().find(
					c => c.targetId === parentChat.targetId
				);

				if (parentChatObj) {
					this.$store.commit('activeChat', parentChatObj);
					await this.$nextTick();
				}

				// 2. 调用 startAgentChat 创建智能体会话
				console.log('[ChatBox] 调用 startAgentChat 创建智能体会话');
				await this.startAgentChat({ agent, member: null });

			} catch (error) {
				console.error('[ChatBox] ❌ 智能体会话创建失败:', error);
				this.$message.error(`智能体加载失败: ${error.message || '未知错误'}`);
			}
		},

		editAgent(agent) {
			// 调用roo-code编辑智能体命令
			this.callRooCodeCommand('roo-cline.editAgent', { agentId: agent.id });
		},

		async exitAgentChat() {
			this.showAgentIframe = false;
			this.currentAgentChat = null;
			this.iframeLoading = false;
			this.$forceUpdate();
			this.$nextTick(() => {
				this.scrollToBottom();
			});
		},
		
		async returnToParentChat() {
			if (!this.currentAgentChat && this.chat.type !== 'AGENT') {
				return;
			}

			// 先获取父会话信息
			let parentChat = null;

			// 1. 从 currentAgentChat 获取
			if (this.currentAgentChat && this.currentAgentChat.parentChat) {
				parentChat = this.currentAgentChat.parentChat;
			}

			// 2. 从当前 chat 对象获取
			if (!parentChat && this.chat.type === 'AGENT') {
				// 使用 parentType 和 parentId 查找
				if (this.chat.parentType && this.chat.parentId) {
					parentChat = this.$store.getters.findChats().find(c =>
						c.type === this.chat.parentType &&
						String(c.targetId) === String(this.chat.parentId)
					);
				}

				// 兼容旧数据：从 targetId 解析
				if (!parentChat && this.chat.targetId && this.chat.targetId.includes('_agent_')) {
					const parts = this.chat.targetId.split('_agent_');
					if (parts.length === 2) {
						const prefix = parts[0];
						let parentType, parentId;

						if (prefix.startsWith('PRIVATE_')) {
							parentType = 'PRIVATE';
							parentId = prefix.substring('PRIVATE_'.length);
						} else if (prefix.startsWith('GROUP_')) {
							parentType = 'GROUP';
							parentId = prefix.substring('GROUP_'.length);
						}

						if (parentType && parentId) {
							parentChat = this.$store.getters.findChats().find(c =>
								c.type === parentType &&
								String(c.targetId) === String(parentId)
							);
						}
					}
				}
			}

			// 退出智能体对话
			await this.exitAgentChat();

			// 如果找到父会话，激活它
			if (parentChat) {
				const chats = this.$store.getters.findChats();
				const parentIndex = chats.findIndex(c =>
					c.type === parentChat.type &&
					String(c.targetId) === String(parentChat.targetId)
				);
				if (parentIndex !== -1) {
					this.$store.commit('activeChat', parentIndex);
				} else {
					console.warn('[ChatBox] 找不到父会话索引:', parentChat);
				}
			} else {
				console.warn('[ChatBox] 无法找到父会话');
			}
		},
		
		async quickStartAgentChat(agent) {
			console.log('[ChatBox] 🎯 quickStartAgentChat 被调用，agent:', agent?.agentName);
			await this.startAgentChat({ agent, chat: this.chat });
		},

		/**
		 * 🔥 处理任务完成事件，保存 renderItems 到 _finalContent
		 */
		onTaskCompleted(data) {
			const { msgInfo, renderItems } = data;

			// 🔥 只保存 renderItems，不保存重复的 merged 内容（节省空间）
			msgInfo._finalContent = {
				renderItems: JSON.parse(JSON.stringify(renderItems))  // 深拷贝，避免引用问题
			};

			// 🔥 使用 $nextTick 确保数据已保存后再删除增量数组
			this.$nextTick(() => {
				// 🔥 删除增量数组，释放内存
				delete msgInfo._structuredChunks;

				// 🔥 标记为已最终化
				msgInfo._isFinalized = true;

				console.log('[ChatBox] 已最终化结构化消息:', {
					msgId: msgInfo.tmpId,
					renderItems: msgInfo._finalContent.renderItems.length
				});
			});
		},

		/**
		 * 🔥 处理智能体任务详情面板打开事件
		 */
		handleOpenAgentTaskDetail(data) {
			const { taskStatusData } = data;
			console.log('[ChatBox] 打开智能体任务详情面板:', taskStatusData);
			
			// 🔥 直接打开详情面板
			this.agentTaskDetailPanelVisible = true;
			this.currentTaskStatusData = taskStatusData;
		},
		
		async callRooCodeCommand(command, params) {
			// 通过VSCode通信调用roo-code命令
			return new Promise((resolve, reject) => {
				const messageId = Date.now() + Math.random();

				const handler = (event) => {
					if (event.data.id === messageId) {
						window.removeEventListener('message', handler);
						if (event.data.error) {
							reject(new Error(event.data.error));
						} else {
							resolve(event.data.result || {});
						}
					}
				};

				window.addEventListener('message', handler);

				// 🔥 移除超时限制：某些任务执行时间较长，不应该有超时限制
				// 如果命令真的失败，会通过 event.data.error 返回错误

				window.parent.postMessage({
					type: 'vscode-command',
					command: command,
					params: params,
					id: messageId
				}, '*');
			});
		},
		
		// 切换侧边栏显示
		toggleSidePanel() {
			// 根据聊天类型决定显示内容
			if (this.chat.type === 'GROUP') {
				// 群聊：显示侧边栏，包含智能体和成员tab
				this.showSide = !this.showSide;
				this.showAgentSide = false;
			} else {
				// 私聊：只显示智能体列表
				this.showAgentSide = !this.showAgentSide;
				this.showSide = false;
			}

			// 重新加载顶部智能体数据，确保与侧面板数据同步
			this.$nextTick(() => {
				this.loadRecentAgents();
			});
		},

		// 隐藏智能体侧面板
		hideAgentSidePanel() {
			this.showAgentSide = false;
			this.showSide = false;
		},

		// 处理头像鼠标进入事件
		handleAvatarMouseEnter(agent, event) {
			
			// 如果头像还未完全展开，等待展开完成后再显示面板
			if (!this.avatarsFullyExpanded) {
				// 保存元素引用和位置信息（不保存event对象，因为它会失效）
				const element = event.currentTarget;
				const rect = element.getBoundingClientRect();
				this.pendingHoverCard = { 
					agent, 
					element,
					position: {
						x: rect.left + rect.width / 2,
						y: rect.bottom - 2  // 向上提升2px
					}
				};
				
				// 设置一个检查定时器
				if (this.hoverCheckTimeout) {
					clearTimeout(this.hoverCheckTimeout);
				}
				this.hoverCheckTimeout = setTimeout(() => {
					// 再次检查是否已经展开
					if (this.avatarsFullyExpanded && this.pendingHoverCard) {
						const { agent, element, position } = this.pendingHoverCard;
						// 检查鼠标是否还在元素上
						if (element && element.matches(':hover')) {
							// 直接设置卡片，不再调用showAgentCard
							this.clearHideTimeout();
							this.hoveredAgentCard = {
								agent: agent,
								x: position.x,
								y: position.y
							};
						}
						this.pendingHoverCard = null;
					}
				}, 400); // 等待展开动画完成
			} else {
				// 已经展开，直接显示
				this.showAgentCard(agent, event);
			}
		},
		
		// 显示智能体卡片
		showAgentCard(agent, event) {
			
			// 清除之前的隐藏定时器
			this.clearHideTimeout();
			
			// 获取头像元素的位置
			const rect = event.currentTarget.getBoundingClientRect();
			
			this.hoveredAgentCard = {
				agent: agent,
				x: rect.left + rect.width / 2, // 头像中心X坐标
				y: rect.bottom - 2 // 头像底部Y坐标，向上提升2px
			};
		},
		
		// 隐藏智能体卡片（带延迟）
		hideAgentCard() {
			// 清除待处理的hover
			this.pendingHoverCard = null;
			if (this.hoverCheckTimeout) {
				clearTimeout(this.hoverCheckTimeout);
				this.hoverCheckTimeout = null;
			}
			
			// 设置延迟隐藏，给用户时间移动到卡片上
			this.hoverCardTimeout = setTimeout(() => {
				this.hoveredAgentCard = null;
				
				// 卡片隐藏后，检查是否需要收缩头像
				// 给一个小延迟，确保用户已经离开
				setTimeout(() => {
					// 如果鼠标不在头像容器上，则收缩（注意：类名已更改）
					const container = document.querySelector('.agent-avatars-container');
					const animatedWrapper = document.querySelector('.agent-avatars-animated');
					const fixedWrapper = document.querySelector('.agent-avatar-fixed');
					
					// 检查鼠标是否在任何头像相关元素上
					if (container && !container.matches(':hover') && 
						(!animatedWrapper || !animatedWrapper.matches(':hover')) &&
						(!fixedWrapper || !fixedWrapper.matches(':hover'))) {
						this.avatarsFullyExpanded = false;
						this.avatarsExpanded = false;
					}
				}, 100);
			}, 200);
		},
		
		// 清除隐藏定时器
		clearHideTimeout() {
			if (this.hoverCardTimeout) {
				clearTimeout(this.hoverCardTimeout);
				this.hoverCardTimeout = null;
			}
		},
		
		// 鼠标进入面板
		onPanelEnter() {
			this.clearHideTimeout();
			// 保持头像展开
			if (this.avatarExpandTimeout) {
				clearTimeout(this.avatarExpandTimeout);
				this.avatarExpandTimeout = null;
			}
		},
		
		// 鼠标离开面板
		onPanelLeave() {
			this.hideAgentCard();
		},
		
		// 防抖包装的加载方法
		loadRecentAgents() {
			// 清除之前的定时器
			if (this.loadAgentsTimer) {
				clearTimeout(this.loadAgentsTimer);
			}

			// 设置新的定时器，300ms 后执行
			this.loadAgentsTimer = setTimeout(() => {
				this.loadRecentAgentsImmediate();
			}, 300);
		},

		// 立即加载智能体（内部方法）
		async loadRecentAgentsImmediate() {
			try {
				// 如果正在加载，跳过重复请求
				if (this.isLoadingAgents) {
					console.log('[ChatBox] 正在加载中，跳过重复请求');
					return;
				}

				// 如果没有chat对象，清空列表并返回
				if (!this.chat) {
					this.recentAgents = [];
					return;
				}

				this.isLoadingAgents = true;
				
				// 根据聊天类型决定加载哪个用户的智能体
				const currentUserId = this.$store.state.userStore.userInfo.id;
				const terminal = this.$store.state.userStore?.userInfo?.terminal || 0;
				let allAgents = [];

				// 判断是否需要加载群组成员的智能体
				const isGroupContext =
					(this.chat.type === 'GROUP') ||
					(this.chat.type === 'AGENT' && this.chat.parentType === 'GROUP');

				console.log('[ChatBox] loadRecentAgents - chat类型:', this.chat.type, 'parentType:', this.chat.parentType, 'parentId:', this.chat.parentId, 'isGroupContext:', isGroupContext);

				if (isGroupContext) {
					// 群聊或群聊下的智能体会话：加载所有群成员的智能体
					console.log('[ChatBox] 群组上下文，加载所有成员的智能体');

					// 获取群组ID
					let groupId = null;
					if (this.chat.type === 'GROUP') {
						groupId = this.chat.targetId;
					} else if (this.chat.type === 'AGENT' && this.chat.parentType === 'GROUP') {
						groupId = this.chat.parentId;
					}

					if (!groupId) {
						console.error('[ChatBox] 无法获取群组ID');
						this.$set(this, 'recentAgents', []);
						return;
					}

					console.log('[ChatBox] 群组ID:', groupId);

					try {
						// 🚀 优化：直接调用新接口 /agent/group/{groupId}
						// 一次性获取所有群成员的智能体，减少API调用次数
						const agents = await this.$http.get(`/agent/group/${groupId}`);
						allAgents = agents || [];
						console.log('[ChatBox] 通过新接口加载了所有群成员的智能体，总数:', allAgents.length);
					} catch (error) {
						console.error('[ChatBox] 加载群组智能体失败:', error);
						allAgents = [];
					}
				} else if (this.chat.type === 'AGENT' && this.chat.parentType === 'PRIVATE') {
					// 智能体会话且父级是私聊：加载对方用户的智能体
					const targetUserId = this.chat.parentId;
					const agents = await this.$http.get(`/agent/user/${targetUserId}`);
					// 🔧 修复：添加 ownerId 映射，与 GROUP 场景保持一致
					allAgents = (agents || []).map(agent => ({
						...agent,
						ownerId: targetUserId
					}));
				} else if (this.chat.type === 'PRIVATE') {
					// 私聊：加载对方的智能体
					const targetUserId = this.chat.targetId;
					const agents = await this.$http.get(`/agent/user/${targetUserId}`);
					// 🔧 修复：添加 ownerId 映射，与 GROUP 场景保持一致
					allAgents = (agents || []).map(agent => ({
						...agent,
						ownerId: targetUserId
					}));
				} else {
					// 其他类型：加载当前用户的智能体
					const agents = await this.$http.get(`/agent/list`);
					allAgents = agents || [];
				}

				// 注意：新的 AgentVO 结构包含 publishStatus 字段
				// 只显示已发布的智能体 (publishStatus === 'PUBLISHED')
				let filteredAgents = (allAgents || []).filter(agent =>
					agent.publishStatus === 'PUBLISHED'
				);

				// 对于群组上下文，显示所有智能体（在线优先排序）
				if (isGroupContext) {
					filteredAgents = filteredAgents
						.sort((a, b) => {
							// 按在线状态排序（在线优先，但不过滤离线）
							// 注意：新API暂不支持在线状态，保留兼容性代码
							const aOnline = (a.publishInfo?.serviceStatus || a.serviceStatus) === 'online';
							const bOnline = (b.publishInfo?.serviceStatus || b.serviceStatus) === 'online';
							if (aOnline && !bOnline) return -1;
							if (!aOnline && bOnline) return 1;

							// 在线状态相同时，按创建时间倒序排列（新创建的在前）
							// 兼容新旧字段名：createdTime (新) 和 createdAt (旧)
							const aCreatedAt = a.createdTime || a.createdAt || 0;
							const bCreatedAt = b.createdTime || b.createdAt || 0;
							return bCreatedAt - aCreatedAt;
						});
					console.log('[ChatBox] 群组上下文，智能体数量:', filteredAgents.length);
				} else {
					// 非群组：按在线状态排序，再按创建时间倒序，显示所有智能体
					filteredAgents = filteredAgents
						.sort((a, b) => {
							// 按在线状态排序
							// 注意：新API暂不支持在线状态，保留兼容性代码
							const aOnline = (a.publishInfo?.serviceStatus || a.serviceStatus) === 'online';
							const bOnline = (b.publishInfo?.serviceStatus || b.serviceStatus) === 'online';
							if (aOnline && !bOnline) return -1;
							if (!aOnline && bOnline) return 1;

							// 在线状态相同时，按创建时间倒序排列（新创建的在前）
							// 兼容新旧字段名：createdTime (新) 和 createdAt (旧)
							const aCreatedAt = a.createdTime || a.createdAt || 0;
							const bCreatedAt = b.createdTime || b.createdAt || 0;
							return bCreatedAt - aCreatedAt;
						});
				}
				
				// 使用 Vue.set 确保响应性
				this.$set(this, 'recentAgents', filteredAgents);

			} catch (error) {
				console.error('[ChatBox] Failed to load recent agents:', error);
				this.$set(this, 'recentAgents', []);
			} finally {
				this.isLoadingAgents = false;
			}
		},
		
		// 展开头像
		expandAvatars() {
			// 清除收缩定时器
			if (this.avatarExpandTimeout) {
				clearTimeout(this.avatarExpandTimeout);
				this.avatarExpandTimeout = null;
			}
			
			// 立即开始展开动画
			this.avatarsExpanded = true;
			
			// 动画完成后（300ms）才允许显示hover面板
			if (this.avatarFullyExpandTimeout) {
				clearTimeout(this.avatarFullyExpandTimeout);
			}
			this.avatarFullyExpandTimeout = setTimeout(() => {
				if (this.avatarsExpanded) {
					this.avatarsFullyExpanded = true;
				}
			}, 350); // 稍微增加延迟确保动画完成
		},
		
		// 收缩头像
		collapseAvatars() {
			// 如果hover面板正在显示，不要收缩
			if (this.hoveredAgentCard) {
				return;
			}
			
			// 清除展开完成定时器
			if (this.avatarFullyExpandTimeout) {
				clearTimeout(this.avatarFullyExpandTimeout);
				this.avatarFullyExpandTimeout = null;
			}
			
			// 立即禁止hover面板
			this.avatarsFullyExpanded = false;
			
			// 延迟收缩，给用户时间移动到具体头像上
			this.avatarExpandTimeout = setTimeout(() => {
				// 再次检查是否有面板显示
				if (!this.hoveredAgentCard) {
					this.avatarsExpanded = false;
				}
			}, 300);
		},

		// iframe 加载完成事件
		onIframeLoad() {
			console.log('[ChatBox] iframe 加载完成');
			
			// 延迟隐藏 loading，确保 iframe 内容完全渲染
			setTimeout(() => {
				this.iframeLoading = false;
			}, 300);
			
			// 初始化通信桥接
			this.$nextTick(() => {
				this.initIframeBridge();
				
				// 延迟发送初始化消息，确保 iframe 完全就绪
				setTimeout(() => {
					this.sendInitToIframe();
					// 同时发送用户信息和智能体信息（如果已准备好）
					this.syncUserInfoToIframe();
					this.syncAgentInfoToIframe();
					this.syncImSessionInfoToIframe();
				}, 500);
			});
		},

		// 初始化 iframe 通信桥接
		initIframeBridge() {
			if (!this.iframeBridge) {
				return;
			}

			// 等待 iframe 元素挂载
			this.$nextTick(() => {
				// 优先使用 ref，如果没有则使用 querySelector
				const iframe = this.$refs.agentIframe || this.$el.querySelector('.agent-iframe');
				if (iframe) {
					this.iframeBridge.setIframe(iframe);

					// 监听 iframe 就绪事件
					this.iframeBridge.on('ready', () => {
						console.log('[ChatBox] iframe 通信桥接已就绪');
						// iframe 就绪后发送初始化消息
						this.sendInitToIframe();
					});

					// 监听智能体会话状态更新
					this.iframeBridge.on('sessionStatusUpdate', this.handleSessionStatusUpdate);

					// 监听用户消息发送事件
					this.iframeBridge.on('userMessageSent', this.handleUserMessageSent);

					// 监听导航到IM会话事件
					this.iframeBridge.on('navigateToImSession', this.handleNavigateToImSession);

					// 监听来自 happy iframe 的退出登录请求
					this.iframeBridge.on('userLogoutRequest', this.handleUserLogoutRequest);
				} else {
					console.warn('[ChatBox] 未找到 iframe 元素，将在 showAgentIframe 变化时重试');
				}
			});
		},

		// 处理智能体会话状态更新
		handleSessionStatusUpdate(data) {
			console.log('[ChatBox] 收到会话状态更新:', data);

			const { sessionId, imChatId, status } = data;

			// 更新到 Vuex store
			this.$store.commit('updateAgentSessionStatus', {
				sessionId,
				status
			});

			// 如果携带了 imChatId，建立映射关系
			if (imChatId) {
				console.log('[ChatBox] 建立映射关系:', { imChatId, happySessionId: sessionId });
				this.$store.commit('setHappySessionMapping', {
					imChatId: imChatId,
					happySessionId: sessionId
				});
			} else {
				console.warn('[ChatBox] ⚠️ 未携带 imChatId，无法建立映射');
			}

			// 如果是当前激活的智能体会话，可以触发额外的 UI 反馈
			if (this.currentAgentChat && this.currentAgentChat.id === sessionId) {
				console.log('[ChatBox] 当前会话状态更新:', status.state);
			}
		},

		// 处理用户消息发送事件
		async handleUserMessageSent(data) {
			console.log('[ChatBox] 收到用户消息发送事件:', data);

			const { agentId, message, messageTime } = data;

			// 直接使用当前 chat 对象的 targetId（已经是正确的格式）
			const targetId = this.chat?.targetId;

			if (!targetId) {
				console.error('[ChatBox] ❌ 无法获取 targetId，this.chat:', this.chat);
				return;
			}

			console.log('[ChatBox] 调用API保存消息:', { targetId, message, messageTime });

			try {
				// 调用后端API保存最后一条消息
				await this.$http({
					url: '/message/agent/chat/last-message',
					method: 'PUT',
					data: {
						targetId,
						message,
						messageTime
					}
				});

				console.log('[ChatBox] ✅ 消息已保存到后端');

				// 立即更新本地 chat 对象
				if (this.chat) {
					this.$set(this.chat, 'lastMessage', message);
					this.$set(this.chat, 'lastContent', message);
					this.$set(this.chat, 'lastSendTime', new Date(messageTime).getTime());

					console.log('[ChatBox] ✅ 本地 chat 对象已更新:', {
						targetId,
						lastMessage: this.chat.lastMessage,
						lastSendTime: this.chat.lastSendTime
					});
				}

				// 触发会话列表重新排序
				this.$store.commit('sortChats');
			} catch (error) {
				console.error('[ChatBox] ❌ 保存消息失败:', error);
			}
		},

		// 处理来自 happy iframe 的退出登录请求
		handleUserLogoutRequest(data) {
			console.log('[ChatBox] 收到来自 happy iframe 的退出登录请求:', data);
			// 使用 eventBus 触发退出事件，由 Home.vue 统一处理
			this.$eventBus.$emit('happy-logout-request');
		},

		// 处理导航到IM会话事件
		handleNavigateToImSession(data) {
			const { sessionId } = data;

			if (!sessionId) {
				return;
			}

			// 从 chatStore.chats 中查找智能体会话
			const chatIndex = this.$store.state.chatStore.chats.findIndex(c =>
				c.type === 'AGENT' && c.targetId === sessionId
			);

			if (chatIndex !== -1) {
				// 直接通过 store commit 激活会话
				this.$store.commit("activeChat", chatIndex);
			}
		},

		// 发送初始化消息到 iframe
		sendInitToIframe() {
			if (!this.iframeBridge) {
				return;
			}

			// 即使 isReady 为 false，也尝试发送（消息会进入队列）
			const userInfo = this.getUserInfo();
			const agentInfo = this.getAgentInfo();
			const imSessionInfo = this.getImSessionInfo();

			if (userInfo || agentInfo || imSessionInfo) {
				this.iframeBridge.sendInit(userInfo, agentInfo, imSessionInfo);
			}
		},

		// 同步用户信息到 iframe
		syncUserInfoToIframe() {
			if (!this.iframeBridge) {
				return;
			}

			const userInfo = this.getUserInfo();
			if (userInfo) {
				// 即使 isReady 为 false，也尝试发送（消息会进入队列）
				this.iframeBridge.sendUserInfoUpdate(userInfo);
			}
		},

		// 同步智能体信息到 iframe
		syncAgentInfoToIframe() {
			if (!this.iframeBridge) {
				return;
			}

			const agentInfo = this.getAgentInfo();
			if (agentInfo) {
				// 即使 isReady 为 false，也尝试发送（消息会进入队列）
				this.iframeBridge.sendAgentInfoUpdate(agentInfo);

				// 🔥 修复：切换智能体时隐藏 loading（因为 iframe 不会重新加载）
				// 延迟隐藏，确保 happy 应用有时间处理
				setTimeout(() => {
					if (this.showAgentIframe) {
						this.iframeLoading = false;
					}
				}, 500);
			}
		},

		// 获取用户信息
		getUserInfo() {
			// Iframe 的登录身份只能来自 userStore。不要复用当前会话的 userInfo，
			// 后者在私聊/智能体会话切换时代表的是对方或智能体。
			const userInfo = this.$store.state.userStore.userInfo;
			if (!userInfo || !userInfo.id) {
				return null;
			}

			// 优先级: sessionStorage → store中的userInfo → localStorage
			const tokenKey =
				sessionStorage.getItem('tokenKey') ||
				userInfo.tokenKey ||
				userInfo.apiKey ||
				localStorage.getItem('tokenKey'); // 最后尝试 localStorage

			const accessToken =
				sessionStorage.getItem('accessToken') ||
				userInfo.accessToken ||
				localStorage.getItem('accessToken');

			const refreshToken =
				sessionStorage.getItem('refreshToken') ||
				userInfo.refreshToken ||
				localStorage.getItem('refreshToken');

			if (tokenKey) {
				console.log('[ChatBox] 获取用户信息，tokenKey来源:', {
					fromSessionStorage: !!sessionStorage.getItem('tokenKey'),
					fromStore: !!userInfo.tokenKey,
					fromLocalStorage: !!localStorage.getItem('tokenKey')
				});
			}

			return {
				id: userInfo.id,
				userId: userInfo.userId || String(userInfo.id),
				username: userInfo.username || userInfo.userName,
				nickName: userInfo.nickName,
				// headImageThumb/headImage 是 IM 登录用户的头像字段；avatar 只是兼容兜底，
				// 避免某些会话对象的 avatar 字段覆盖当前用户头像。
				avatar: userInfo.headImageThumb || userInfo.headImage || userInfo.avatar,
				terminal: userInfo.terminal,
				tokenKey: tokenKey,
				accessToken: accessToken,
				refreshToken: refreshToken,
				apiKey: tokenKey, // 兼容字段
				// 其他可能需要的字段
				phone: userInfo.phone,
				email: userInfo.email,
				isBanned: userInfo.isBanned,
				reason: userInfo.reason
			};
		},

		// 获取智能体信息
		getAgentInfo() {
			const context = this.currentAgentChat || (this.chat.type === 'AGENT' ? this.chat : null);
			if (!context) {
				return null;
			}

			const agentId =
				context.agentId ||
				context.agent?.agentId ||
				(context.targetId && context.targetId.includes('_agent_')
					? context.targetId.split('_agent_')[1]
					: null);

			// 检查是否是 fullscreen 模式（通过 URL 参数）
			const isFullscreen = this.$route.query.fullscreen === 'true';

			// ✅ 优先级 1: 从 sessionStorage 获取完整信息
			const pendingAgentData = sessionStorage.getItem('pendingAgentData');
			if (pendingAgentData) {
				try {
					const agentData = JSON.parse(pendingAgentData);
					if (agentData.agentId === agentId) {
						console.log('[agent share debug] getAgentInfo: 使用 sessionStorage 数据');
						return {
							agentId: agentData.agentId,
							agentName: agentData.agentName,
							avatar: agentData.avatarUrl,
							mode: isFullscreen ? 'fullscreen' : (agentData.mode || 'code'),
							description: agentData.description,
							sessionId: context.targetId || context.id,
							userId: agentData.userId,  // Agent owner's userId for ownership check
							publishSettings: agentData.publishSettings || null
						};
					}
				} catch (e) {
					console.warn('[agent share debug] getAgentInfo: 解析 pendingAgentData 失败:', e);
				}
			}

			// ✅ 优先级 2: 从 recentAgents 查找
			let agent = null;
			if (agentId && Array.isArray(this.recentAgents) && this.recentAgents.length > 0) {
				// 🔧 修复：使用 String() 转换避免类型不匹配（API返回number，targetId解析为string）
				agent = this.recentAgents.find(a => String(a.agentId) === String(agentId));
				console.log('[getAgentInfo] 从 recentAgents 查找结果:', { agentId, found: !!agent, agentUserId: agent?.userId });
			}

			// ✅ 优先级 3: 从 context.agent 获取
			const contextAgent = context.agent;
			console.log('[getAgentInfo] context.agent:', { hasContextAgent: !!contextAgent, contextAgentUserId: contextAgent?.userId });

			// ✅ 优先级 3: 从 context 获取
			const avatar =
				agent?.avatarUrl ||
				context.agent?.avatarUrl ||
				context.headImage ||
				this.chat?.headImage ||
				null;

			const result = {
				agentId: agentId || agent?.agentId,
				agentName: context.agentName || context.agent?.agentName || agent?.agentName || context.showName,
				avatar,
				mode: isFullscreen ? 'fullscreen' : (agent?.mode || context.agent?.mode || context.mode || 'code'),
				description: agent?.modeConfig?.description || agent?.description || context.agent?.description || null,
				sessionId: context.targetId || context.id,
				userId: agent?.userId || agent?.ownerId || contextAgent?.userId || null,  // Agent owner's userId for ownership check
				publishSettings: agent?.publishSettings || contextAgent?.publishSettings || null
			};

			console.log('[getAgentInfo] 最终结果:', { userId: result.userId, publishSettings: !!result.publishSettings });
			return result;
		},

		// 获取 IM 会话信息
		getImSessionInfo() {
			console.log('[ChatBox] getImSessionInfo 被调用，this.chat:', this.chat);

			if (!this.chat) {
				console.warn('[ChatBox] 无法获取 IM 会话信息：chat 为空');
				return null;
			}

			let sessionName = this.chat.showName || this.chat.remarkName || this.chat.name;
			console.log('[ChatBox] 原始会话名:', sessionName);

			// 根据会话类型优化显示名称
			if (this.chat.type === 'GROUP') {
				sessionName = sessionName || `群聊(${this.chat.targetId})`;
			} else if (this.chat.type === 'PRIVATE') {
				sessionName = sessionName || `私聊(${this.chat.targetId})`;
			} else if (this.chat.type === 'AGENT') {
				sessionName = sessionName || `智能体(${this.chat.targetId})`;
			}

			const imSessionInfo = {
				sessionId: this.chat.id || this.chat.targetId,
				sessionType: this.chat.type,
				sessionName: sessionName,
				targetId: this.chat.targetId,
				targetName: this.chat.type === 'PRIVATE' ? sessionName : undefined,
				groupName: this.chat.type === 'GROUP' ? sessionName : undefined,
				participantCount: this.chat.type === 'GROUP' ? (this.groupMembers?.length || 0) : undefined
			};

			console.log('[ChatBox] 生成的 IM 会话信息:', imSessionInfo);
			return imSessionInfo;
		},

		// 同步 IM 会话信息到 iframe
		syncImSessionInfoToIframe() {
			console.log('[ChatBox] syncImSessionInfoToIframe 被调用');
			console.log('[ChatBox] iframeBridge 状态:', !!this.iframeBridge);

			if (!this.iframeBridge) {
				console.warn('[ChatBox] iframeBridge 未初始化，无法同步 IM 会话信息');
				return;
			}

			const imSessionInfo = this.getImSessionInfo();
			console.log('[ChatBox] 获取到的 imSessionInfo:', imSessionInfo);

			if (!imSessionInfo) {
				console.warn('[ChatBox] 无法获取 IM 会话信息');
				return;
			}

			// 深度比较，避免发送相同的信息
			if (this.lastSentImSessionInfo) {
				const isSame =
					this.lastSentImSessionInfo.sessionId === imSessionInfo.sessionId &&
					this.lastSentImSessionInfo.sessionType === imSessionInfo.sessionType &&
					this.lastSentImSessionInfo.sessionName === imSessionInfo.sessionName &&
					this.lastSentImSessionInfo.targetId === imSessionInfo.targetId &&
					this.lastSentImSessionInfo.targetName === imSessionInfo.targetName &&
					this.lastSentImSessionInfo.groupName === imSessionInfo.groupName &&
					this.lastSentImSessionInfo.participantCount === imSessionInfo.participantCount;

				if (isSame) {
					console.log('[ChatBox] IM 会话信息未变化，跳过发送');
					return;
				}
			}

			console.log('[ChatBox] 准备发送 IM 会话信息到 iframe:', imSessionInfo);
			this.iframeBridge.sendImSessionInfoUpdate(imSessionInfo);

			// 缓存本次发送的信息
			this.lastSentImSessionInfo = { ...imSessionInfo };
			console.log('[ChatBox] IM 会话信息已发送并缓存');
		},

		// ===== 文件拖拽支持 =====

		setupFileDragDrop() {
			console.log('[ChatBox] 设置文件拖拽监听器');

			// 监听拖拽事件
			this.$el.addEventListener('dragenter', this.handleDragEnter);
			this.$el.addEventListener('dragover', this.handleDragOver);
			this.$el.addEventListener('dragleave', this.handleDragLeave);
			this.$el.addEventListener('drop', this.handleDrop);
		},

		removeFileDragDrop() {
			console.log('[ChatBox] 清理文件拖拽监听器');

			if (this.$el) {
				this.$el.removeEventListener('dragenter', this.handleDragEnter);
				this.$el.removeEventListener('dragover', this.handleDragOver);
				this.$el.removeEventListener('dragleave', this.handleDragLeave);
				this.$el.removeEventListener('drop', this.handleDrop);
			}

			this.isDragOver = false;
			this.dragCounter = 0;
		},

		handleDragEnter(e) {
			e.preventDefault();
			e.stopPropagation();

			this.dragCounter++;

			// 检查是否有文件
			if (this.hasFiles(e.dataTransfer) && this.dragCounter === 1) {
				this.isDragOver = true;
				console.log('[ChatBox] 拖拽进入，显示覆盖层');
			}
		},

		handleDragOver(e) {
			e.preventDefault();
			e.stopPropagation();
			e.dataTransfer.dropEffect = 'copy';
		},

		handleDragLeave(e) {
			e.preventDefault();
			e.stopPropagation();

			this.dragCounter--;

			if (this.dragCounter === 0) {
				this.isDragOver = false;
				console.log('[ChatBox] 拖拽离开，隐藏覆盖层');
			}
		},

		async handleDrop(e) {
			e.preventDefault();
			e.stopPropagation();

			this.isDragOver = false;
			this.dragCounter = 0;

			console.log('[ChatBox] 文件拖拽放下');

			// 获取文件
			const files = Array.from(e.dataTransfer.files);

			if (files.length === 0) {
				console.log('[ChatBox] 未检测到文件，尝试解析VSCode拖拽数据');
				// 尝试解析VSCode拖拽数据
				const filePaths = await this.parseVSCodeDragData(e.dataTransfer);
				if (filePaths.length > 0) {
					console.log('[ChatBox] 解析出VSCode文件路径:', filePaths);
					await this.handleVSCodeFilePaths(filePaths);
					return;
				}

				console.warn('[ChatBox] 未找到可用文件');
				this.$message?.warning('未检测到文件');
				return;
			}

			console.log('[ChatBox] 拖拽文件:', files.map(f => f.name));

			// 处理文件
			await this.processDroppedFiles(files);
		},

		hasFiles(dataTransfer) {
			if (!dataTransfer) return false;

			// 检查files集合
			if (dataTransfer.files && dataTransfer.files.length > 0) {
				return true;
			}

			// 检查types
			if (dataTransfer.types) {
				const hasFileType = dataTransfer.types.includes('Files') ||
					dataTransfer.types.includes('application/x-vscode-file') ||
					dataTransfer.types.includes('application/vnd.code.uri-list');
				if (hasFileType) {
					return true;
				}
			}

			// 检查items
			if (dataTransfer.items && dataTransfer.items.length > 0) {
				for (let i = 0; i < dataTransfer.items.length; i++) {
					if (dataTransfer.items[i].kind === 'file') {
						return true;
					}
				}
			}

			return false;
		},

		async parseVSCodeDragData(dataTransfer) {
			const filePaths = [];

			console.log('[ChatBox] 解析VSCode拖拽数据，可用类型:', Array.from(dataTransfer.types));

			// VSCode内部URI列表
			const internalUriList = dataTransfer.getData('application/vnd.code.uri-list') ||
				dataTransfer.getData('application/vnd.code.tree.resourcecontext');
			if (internalUriList) {
				try {
					const uris = internalUriList.split(/[\r\n]+/).filter(uri => uri.trim() && !uri.startsWith('#'));
					for (const uri of uris) {
						const path = uri.replace('file://', '');
						filePaths.push(path);
					}
				} catch (e) {
					console.warn('[ChatBox] 解析内部URI失败:', e);
				}
			}

			// 标准text/uri-list
			if (filePaths.length === 0) {
				const uriList = dataTransfer.getData('text/uri-list');
				if (uriList) {
					const uris = uriList.split(/[\r\n]+/).filter(uri => uri.trim() && !uri.startsWith('#'));
					for (const uri of uris) {
						const path = uri.replace('file://', '');
						filePaths.push(path);
					}
				}
			}

			// text/plain格式
			if (filePaths.length === 0) {
				const plainText = dataTransfer.getData('text/plain');
				if (plainText && (plainText.includes('/') || plainText.includes('\\'))) {
					const lines = plainText.split('\n').filter(path =>
						path.trim() && (path.includes('/') || path.includes('\\'))
					);
					filePaths.push(...lines);
				}
			}

			// 清理路径
			const cleanPaths = filePaths
				.map(path => path.trim())
				.filter(path => path && path !== '')
				.map(path => path.replace(/^file:\/\//, ''))
				.filter((path, index, arr) => arr.indexOf(path) === index);

			return cleanPaths;
		},

		async handleVSCodeFilePaths(filePaths) {
			console.log('[ChatBox] 处理VSCode文件路径:', filePaths.length, '个');

			if (window.vscodeServices && window.vscodeServices.get) {
				try {
					const sessionInfo = this.getCurrentSessionInfo();
					const results = await window.vscodeServices.get('IChatWorkspaceService').uploadFiles(filePaths, sessionInfo);

					let successCount = 0;
					for (const result of results) {
						if (result.success) {
							successCount++;
							console.log('[ChatBox] 文件上传成功:', result.message);
						} else {
							console.error('[ChatBox] 文件上传失败:', result);
							this.$message?.error(result.message);
						}
					}

					if (successCount > 0) {
						this.$message?.success(`成功发送 ${successCount} 个文件`);
					}
				} catch (error) {
					console.error('[ChatBox] VSCode文件上传失败:', error);
					this.$message?.error(`文件上传失败: ${error.message}`);
				}
			} else {
				this.$message?.warning('当前环境不支持VSCode文件路径上传');
			}
		},

		getCurrentSessionInfo() {
			if (!this.chat) return null;

			return {
				id: this.chat.id || this.chat.targetId,
				targetId: this.chat.targetId,
				type: this.chat.type || 'PRIVATE',
				name: this.chat.showName || '聊天',
				showName: this.chat.showName || '聊天',
				headImage: this.chat.headImage || '',
				participants: [this.chat.targetId]
			};
		},

		async processDroppedFiles(files) {
			console.log('[ChatBox] 处理拖拽文件:', files.length, '个');

			// 过滤空文件
			const validFiles = files.filter(file => {
				if (file.size === 0) {
					this.$message?.warning(`文件 ${file.name} 为空，已跳过`);
					return false;
				}
				return true;
			});

			if (validFiles.length === 0) {
				console.log('[ChatBox] 没有有效文件需要上传');
				return;
			}

			try {
				// 获取当前会话信息
				const sessionInfo = this.getCurrentSessionInfo();
				if (!sessionInfo) {
					throw new Error('未找到当前会话信息');
				}

				// 🚀 VSCode环境：使用批量上传方法
				if (window.vscodeServices && window.vscodeServices.get) {
					console.log('[ChatBox] VSCode环境 - 批量文件上传:', validFiles.length, '个文件');
					const results = await window.vscodeServices.get('IChatWorkspaceService').uploadFiles(validFiles, sessionInfo);

					// 处理上传结果
					let successCount = 0;
					let failCount = 0;

					for (const result of results) {
						if (result.success) {
							successCount++;
							console.log('[ChatBox] ✅ 文件上传成功:', result.message);
						} else {
							failCount++;
							console.error('[ChatBox] ❌ 文件上传失败:', result);
							this.$message?.error(result.message);
						}
					}

					// 显示总体结果
					if (successCount > 0) {
						this.$message?.success(`成功发送 ${successCount} 个文件`);
					}
					if (failCount > 0) {
						console.error(`[ChatBox] ${failCount} 个文件上传失败`);
					}

				} else {
					// Web环境：并行发送所有文件
					console.log('[ChatBox] Web环境 - 并行发送文件:', validFiles.length, '个');

					const uploadPromises = validFiles.map(file => {
						// 判断文件类型并返回发送Promise
						if (file.type.startsWith('image/')) {
							return this.sendImageMessage(file).catch(error => {
								console.error('[ChatBox] 图片发送失败:', file.name, error);
								return { success: false, file: file.name };
							});
						} else {
							return this.sendFileMessage(file).catch(error => {
								console.error('[ChatBox] 文件发送失败:', file.name, error);
								return { success: false, file: file.name };
							});
						}
					});

					// 并行等待所有文件上传完成
					const results = await Promise.all(uploadPromises);

					// 统计成功和失败数量
					const failedFiles = results.filter(r => r && r.success === false);
					const successCount = validFiles.length - failedFiles.length;

					if (successCount > 0) {
						this.$message?.success(`成功发送 ${successCount} 个文件`);
					}
					if (failedFiles.length > 0) {
						this.$message?.error(`${failedFiles.length} 个文件发送失败`);
					}
				}

			} catch (error) {
				console.error('[ChatBox] 文件处理失败:', error);
				this.$message?.error(`文件发送失败: ${error.message}`);
			}
		}
	},
	computed: {
		// 判断是否为 OpenMe 终端会话
		isOpenMeTerminal() {
			return this.chat && this.chat.senderTerminal === 6;
		},

		// OpenMe iframe URL - 自动携带用户信息实现免登录
		openmeIframeUrl() {
			// 云电脑订阅用户走 vnc gateway，否则走本地 openme
			if (this.hasCloudPc && this.useCloudPc && this.mine && this.mine.id) {
				return `https://18789-${this.mine.id}.vnc.shadanai.com/#token=276f6078de595da4b3402eea9ed9c0e61dbf7612b8f705cc31d65d6104aaef75`;
			}
			const base = 'https://localhost:18800/';
			const params = new URLSearchParams();
			params.set('token', 'openme-76b35bff');
			if (this.mine && this.mine.id) {
				params.set('openid', `im_user_${this.mine.id}`);
				params.set('nickname', this.mine.nickName || this.mine.username || '');
				params.set('avatar', this.mine.headImageThumb || this.mine.headImage || '');
				params.set('activate', this.mine.activate != null ? String(this.mine.activate) : '1');
			}
			return `${base}?${params.toString()}`;
		},
		
		// 智能体iframe URL
		agentIframeUrl() {
			// 基础URL
			let baseUrl;
			if (process.env.NODE_ENV === 'development') {
				baseUrl = 'http://localhost:8081';
			} else {
				baseUrl = 'https://happy.shadanai.com';
			}

			// 🔥 添加移动端参数
			if (this.isMobile) {
				const separator = baseUrl.includes('?') ? '&' : '?';
				return `${baseUrl}${separator}mobile=true`;
			}

			return baseUrl;
		},

		// 根据当前状态决定显示哪些消息
		displayMessages() {
			return this.allMessages.filter(msg => {
				// 过滤空白消息（包括只有换行和零宽字符的消息）
				if (this.$msgType.isNormal(msg.type)) {
					const content = msg.content || '';
					// 移除HTML标签、零宽字符后再判断
					const textContent = content
						.replace(/<[^>]*>/g, '')
						.replace(/[\u200B-\u200D\uFEFF]/g, '')
						.trim();
					return textContent !== '';
				}
				return true;
			});
		},
		
		// 显示的标题
		displayTitle() {
			if (this.currentAgentChat) {
				return this.currentAgentChat.showName;
			}
			return this.title;
		},
		
		// 父级会话名称
		parentChatName() {
			// 1. 优先使用 currentAgentChat 的 parentChat
			if (this.currentAgentChat && this.currentAgentChat.parentChat) {
				return this.currentAgentChat.parentChat.showName;
			}

			// 2. 从当前 chat 对象获取父级信息
			if (this.chat.type === 'AGENT') {
				// 使用 parentType 和 parentId 查找父会话
				if (this.chat.parentType && this.chat.parentId) {
					const parentChat = this.$store.getters.findChats().find(c =>
						c.type === this.chat.parentType &&
						String(c.targetId) === String(this.chat.parentId)
					);
					if (parentChat) {
						return parentChat.showName;
					}
				}

				// 3. 兼容旧数据：从 targetId 解析父级信息
				if (this.chat.targetId && this.chat.targetId.includes('_agent_')) {
					const parts = this.chat.targetId.split('_agent_');
					if (parts.length === 2) {
						const prefix = parts[0];
						let parentType, parentId;

						if (prefix.startsWith('PRIVATE_')) {
							parentType = 'PRIVATE';
							parentId = prefix.substring('PRIVATE_'.length);
						} else if (prefix.startsWith('GROUP_')) {
							parentType = 'GROUP';
							parentId = prefix.substring('GROUP_'.length);
						}

						if (parentType && parentId) {
							const parentChat = this.$store.getters.findChats().find(c =>
								c.type === parentType &&
								String(c.targetId) === String(parentId)
							);
							if (parentChat) {
								return parentChat.showName;
							}
						}
					}
				}
			}

			return '返回';
		},
		
		// 是否在智能体对话模式
		// 合并正常消息和LLM流式消息 - 优化性能版本
		allMessages() {
			// 使用缓存避免频繁重新计算
			const messages = this.chat.messages || [];
			
			// 如果没有活跃的流式消息，直接返回原数组
			if (!this.activeLLMStream) {
				return messages;
			}
			
			// 创建流式消息对象
			const llmMessage = {
				tmpId: this.activeLLMStream.streamId,
				type: 'llm-stream',
				streamId: this.activeLLMStream.streamId,
				title: this.activeLLMStream.title,
				sendTime: this.activeLLMStream.startTime,
				sendId: 0, // AI助手
				sendNickName: 'AI 助手',
				selfSend: false,
				isCompleted: this.activeLLMStream.isCompleted || false
			};
			
			// 浅拷贝数组并插入流式消息
			const result = [...messages];
			
			// 找到正确的插入位置（按时间排序）
			let insertIndex = result.length;
			for (let i = result.length - 1; i >= 0; i--) {
				if (result[i].sendTime <= llmMessage.sendTime) {
					insertIndex = i + 1;
					break;
				}
			}
			
			result.splice(insertIndex, 0, llmMessage);
			return result;
		},
		mine() {
			return this.$store.state.userStore.userInfo;
		},
		isFriend() {
			return this.$store.getters.isFriend(this.userInfo.id);
		},
		friend() {
			return this.$store.getters.findFriend(this.userInfo.id)
		},
		title() {
			let title = this.chat.showName;
			if (this.chat.type == "GROUP") {
				let size = this.groupMembers.filter(m => !m.quit).length;
				title += `(${size})`;
			}
			return title;
		},
		messageAction() {
			// 终端会话使用专门的接口
			if (this.chat.isTerminalChat || this.chat.isTerminalInbox) {
				return `/message/private/send`;
			}
			return `/message/${this.chat.type.toLowerCase()}/send`;
		},
		unreadCount() {
			return this.chat.unreadCount;
		},
		messageSize() {
			return this.allMessages.length;
		},
		isBanned() {
			return (this.chat.type == "PRIVATE" && this.userInfo.isBanned) ||
				(this.chat.type == "GROUP" && this.group.isBanned)
		},
		memberSize() {
			return this.groupMembers.filter(m => !m.quit).length;
		},
		isGroup() {
			return this.chat.type == 'GROUP';
		},
		isPrivate() {
			return this.chat.type == 'PRIVATE';
		},
		agentCardStyle() {
			if (!this.hoveredAgentCard) return {};
			
			// 获取窗口尺寸
			const windowWidth = document.documentElement.clientWidth || 800;
			const windowHeight = document.documentElement.clientHeight || 600;
			
			// 卡片尺寸
			const cardWidth = 320;
			const cardHeight = 380; // 预估高度
			
			// 获取头像位置（相对于头像中心）
			const avatarCenterX = this.hoveredAgentCard.x;
			const avatarBottomY = this.hoveredAgentCard.y;
			
			// 计算卡片水平位置（让箭头对准头像中心）
			let left = avatarCenterX - cardWidth / 2;
			
			// 确保卡片不超出屏幕边界
			if (left < 10) {
				left = 10;
			} else if (left + cardWidth > windowWidth - 10) {
				left = windowWidth - cardWidth - 10;
			}
			
			// 计算箭头偏移（相对于卡片）
			const arrowOffset = avatarCenterX - left - cardWidth / 2;
			
			return {
				top: avatarBottomY + 6 + 'px', // 向上提升2px，从8px改为6px
				left: left + 'px',
				'--arrow-offset': arrowOffset + 'px'
			};
		},

		isMobile() {
			// 优先检查 URL 参数 fullscreen
			if (this.$route.query.fullscreen === 'true') {
				return true;
			}
			// 回退到 User Agent 检测
			return /Android|webOS|iPhone|iPad|iPod/i.test(navigator.userAgent);
		},

		// 🔥 多选模式相关计算属性
		isMultiSelectMode() {
			return this.$store.state.chatStore.multiSelectMode.active;
		},
		selectedCount() {
			return this.$store.state.chatStore.multiSelectMode.selectedMessages.length;
		}
	},
	created() {
		// 查询云电脑订阅状态
		const userId = this.$store.state.userStore.userInfo?.id;
		if (userId) {
			fetch('/wxpay/cloud-pc/status?userId=' + userId)
				.then(r => r.json())
				.then(res => { if (res.result) this.hasCloudPc = true; })
				.catch(() => {});
		}

		// 初始化 Happy iframe 通信桥接
		this.iframeBridge = new HappyIframeBridge();
		console.log('[ChatBox] HappyIframeBridge 已初始化');

		// 初始化智能体提及管理器（API模式，不依赖iframe）
		this.agentMentionManager = getAgentMentionManager();
		console.log('[ChatBox] AgentMentionManager 已初始化（API模式）');

		// 🔥 监听任务已调度事件，更新状态卡片
		this.agentMentionManager.on('taskDispatched', this.handleTaskDispatched);

		// 将 bridge 保存到全局，供其他组件使用（如退出登录时）
		if (typeof window !== 'undefined') {
			window.__imHappyIframeBridge = this.iframeBridge;
		}

		// 如果初始 chat 是智能体会话，手动触发 iframe 模式
		if (this.chat && (this.chat.type === 'AGENT' || (this.chat.targetId && this.chat.targetId.toString().includes('agent_')))) {
			console.log('[ChatBox] 检测到智能体会话，手动触发加载');
			this.$nextTick(() => {
				this.activateAgentChatFromChat(this.chat);
			});
		}
	},
	mounted() {

		// 组件已成功挂载

		// 🔧 修复：确保scroll事件正确绑定
		this.$nextTick(() => {
			const scrollBox = document.getElementById('chatScrollBox');
			if (scrollBox) {
				// 移除可能存在的旧事件监听器
				scrollBox.removeEventListener('scroll', this.onScroll);
				// 重新绑定事件监听器
				scrollBox.addEventListener('scroll', this.onScroll);
			}
		});

		// 注册LLM流式消息事件监听
		this.registerLLMStreamHandlers();

		// 加载最近使用的智能体
		this.loadRecentAgents();

		// 初始化 iframe 通信桥接
		this.initIframeBridge();

		// 🆕 设置文件拖拽支持
		this.setupFileDragDrop();

		// 🎯 监听智能体分享链接的会话创建事件
		this.$eventBus.$on('start-agent-from-share', this.handleStartAgentFromShare);
	},
	beforeDestroy() {
		// 清理智能体提及管理器
		if (this.agentMentionManager) {
			// 🔥 移除事件监听
			this.agentMentionManager.off('taskDispatched', this.handleTaskDispatched);
			destroyAgentMentionManager();
			this.agentMentionManager = null;
			console.log('[ChatBox] AgentMentionManager 已清理');
		}

		// 清理 iframe 通信桥接
		if (this.iframeBridge) {
			// 移除状态监听器
			this.iframeBridge.off('sessionStatusUpdate', this.handleSessionStatusUpdate);
			this.iframeBridge.off('userMessageSent', this.handleUserMessageSent);
			this.iframeBridge.off('navigateToImSession', this.handleNavigateToImSession);
			this.iframeBridge.off('userLogoutRequest', this.handleUserLogoutRequest);

			this.iframeBridge.destroy();
			console.log('[ChatBox] HappyIframeBridge 已清理');

			// 清除全局引用
			if (typeof window !== 'undefined') {
				delete window.__imHappyIframeBridge;
			}
		}

		// 清理防抖定时器
		if (this.loadAgentsTimer) {
			clearTimeout(this.loadAgentsTimer);
		}

		// 清理事件监听器
		const scrollBox = document.getElementById('chatScrollBox');
		if (scrollBox) {
			scrollBox.removeEventListener('scroll', this.onScroll);
		}

		// 清理输入框拖拽事件监听器
		document.removeEventListener('mousemove', this.handleInputResizerMouseMove);
		document.removeEventListener('mouseup', this.handleInputResizerMouseUp);

		// 🎯 清理智能体分享链接事件监听
		this.$eventBus.$off('start-agent-from-share', this.handleStartAgentFromShare);

		// 🆕 清理文件拖拽监听器
		this.removeFileDragDrop();
	},
	watch: {
		chat: {
			async handler(newChat, oldChat) {
				// 切换到非 OpenMe 终端时隐藏 iframe，但保留启动状态
				if (newChat && newChat.senderTerminal !== 6) {
					this.showOpenMeIframe = false;
				}
				// 切换到 OpenMe 终端时，检查在线状态
				else if (newChat && newChat.senderTerminal === 6) {
					const isOnline = await this.checkOpenMeTerminalStatus();
					// 如果在线，直接显示 iframe
					if (isOnline) {
						this.showOpenMeIframe = true;
						this.openmeSessionStarted = true;
					} else {
						this.showOpenMeIframe = false;
					}
				}
				
				// added by qinkee.!oldChat为组件初始化加载，(newChat.type + newChat.targetId != oldChat.type + oldChat.targetId)为会话切换
				if (!oldChat || (newChat.type + newChat.targetId != oldChat.type + oldChat.targetId)) {
					// 清除之前会话的活跃LLM流
					if (this.activeLLMStream) {
						this.activeLLMStream = null;
						this.llmStreams.clear();
					}

					// 清除上次发送的 IM 会话信息缓存，确保新会话的第一次同步总是发送
					this.lastSentImSessionInfo = null;

					// 如果从智能体会话切换出去，需要保存并清理
					if (this.currentAgentChat && newChat.type !== 'AGENT' && (!newChat.targetId || !newChat.targetId.toString().includes('agent_'))) {
						// 保存当前智能体会话
						await this.exitAgentChat();
					}
					
					// 🔧 根据会话类型处理不同逻辑
					if (this.chat.type == "GROUP") {
						// 群组：先加载群组信息和成员，再加载智能体（在loadGroup中完成）
						this.loadGroup(this.chat.targetId);
					} else if (this.chat.isTerminalChat || this.chat.isTerminalInbox) {
						// 🔧 终端收件箱会话的特殊处理

						// 终端会话不需要加载好友信息，使用当前用户信息作为userInfo
						this.userInfo = this.mine;
						// 终端会话不需要验证好友关系，可以直接发送消息

						// 加载智能体列表
						this.$nextTick(() => {
							this.loadRecentAgents();
						});
					} else if (this.chat.type === "AGENT" || (this.chat.targetId && this.chat.targetId.toString().includes('agent_'))) {
						// 智能体会话：直接进入 iframe 模式
						this.userInfo = {
							id: this.chat.targetId,
							nickName: this.chat.showName || '智能体',
							headImageThumb: this.chat.headImage
						};

					// 如果正在初始化，跳过 chat watcher 的初始化逻辑
					if (this.isInitializingAgent) {
						// 保持初始化状态
						this.showAgentIframe = true;
						this.agentInitProgress.show = true;

						// 🔥 关键修复：从 chat 对象恢复 currentAgentChat 状态
						if (!this.currentAgentChat && this.chat.agentId) {
							const parentChat = this.findParentChatByTargetId(this.chat.targetId);
							this.currentAgentChat = {
								agentId: this.chat.agentId,
								agentName: this.chat.agentName,
								agent: this.chat.agent,
								parentChat,
								targetId: this.chat.targetId,
								showName: this.chat.showName,
								headImage: this.chat.headImage,
								contextType: 'embedded'
							};
							console.log('[agent share debug] ✅ 从 chat 恢复 currentAgentChat:', this.currentAgentChat);
						}

						console.log('[agent share debug] ✅ 设置 showAgentIframe = true, agentInitProgress.show = true');
						return;
					}

					// 🔥 会话切换场景：使用简单loading
					console.log('[ChatBox] 智能体会话切换，显示简单loading');
					this.showAgentIframe = true;
					this.iframeLoading = true; // 显示简单loading
					// 不设置 agentInitProgress.show，避免显示完整面板

					try {
						// 步骤1: 用户信息（静默等待）
						await this.waitForMineReady();

						// 步骤2: VNC Pod 同步（静默执行）
						await this.syncVncChatsOnAgentStart();

						// 🔥 步骤3: 先加载智能体列表，确保 getAgentInfo 能获取完整信息（包括 userId 和 publishSettings）
						await this.loadRecentAgentsImmediate();

						// 步骤4: 激活会话（快速切换）
						this.activateAgentChatFromChat(this.chat);

						// 切换完成，隐藏loading
						this.$nextTick(() => {
							setTimeout(() => {
								this.iframeLoading = false;
							}, 300);
						});

					} catch (error) {
						console.error('[ChatBox] 会话切换失败:', error);
						this.$message.error(`智能体初始化失败: ${error.message || '云电脑未就绪'}`);
						this.iframeLoading = false;
						this.showAgentIframe = false;
					}
					} else {
						// PRIVATE和其他类型
						this.loadFriend(this.chat.targetId);
						// 加载已读状态
						this.loadReaded(this.chat.targetId);

						// 加载智能体列表
						this.$nextTick(() => {
							this.loadRecentAgents();
						});
					}

					// 滚到底部
					this.scrollToBottom(true); // 切换聊天，强制滚动
					this.showSide = false;
					// 消息已读
					this.readedMessage()
					// 初始状态只显示30条消息
					let messages = this.chat.messages || [];
					let size = messages.length;
					this.showMinIdx = size > 30 ? size - 30 : 0;
					// 重置输入框
					this.resetEditor();
					// 复位回执消息
					this.isReceipt = false;
					// 更新placeholder
					this.refreshPlaceHolder();
				}
			},
			immediate: true
		},
		'$route.query': {
			immediate: true,
			deep: true,
			async handler(query, oldQuery) {
				console.log('[agent share debug] ========================================');
				console.log('[agent share debug] 🔍 $route.query watcher 被触发');
				console.log('[agent share debug] query.agentId:', query.agentId);
				console.log('[agent share debug] query.autoStart:', query.autoStart, 'type:', typeof query.autoStart);
				console.log('[agent share debug] oldQuery:', oldQuery);
				console.log('[agent share debug] oldQuery?.autoStart:', oldQuery?.autoStart);
				console.log('[agent share debug] ========================================');

				// 防止重复触发
				if (query.agentId && query.autoStart === 'true' &&
					(!oldQuery || oldQuery.autoStart !== 'true')) {

					// 防止重复初始化
					if (this.isInitializingAgent) {
						console.warn('智能体正在初始化中，忽略本次请求');
						return;
					}
					this.isInitializingAgent = true;

					// 调用统一初始化入口
					try {
						await this.initAgentSession(query.agentId, {
							fromShare: true
						});

						// 清理 URL 参数
						this.$router.replace({
							query: {
								...this.$route.query,
								autoStart: undefined
							}
						});
					} catch (error) {
						console.error('自动启动失败:', error);
					} finally {
						this.isInitializingAgent = false;
					}
				}
			}
		},

		// 监听用户信息变化（保留用于其他可能的逻辑）
		mine: {
			immediate: false,
			handler(newMine, oldMine) {
				// 只在用户信息从无到有时触发
				if (newMine && newMine.id && (!oldMine || !oldMine.id)) {
					console.log('[agent share debug] 用户信息已就绪:', newMine.id);
					// ✅ pendingAutoStart 逻辑已移除
					// 现在由 waitForMineReady() 的 watcher 自动处理用户信息就绪
				}
			}
		},
		messageSize: {
			handler(newSize, oldSize) {
				if (newSize > oldSize) {
					// 拉至底部
					this.scrollToBottom(true) // 切换聊天，强制滚动;
				}
			}
		},
		// 监听 showAgentIframe 变化，初始化 iframe 通信
		showAgentIframe: {
			handler(newVal) {
				if (newVal) {
					// iframe 显示时，显示 loading 状态
					this.iframeLoading = true;
					// iframe 加载完成后会自动调用 onIframeLoad
				} else {
					// iframe 隐藏时，重置 loading 状态
					this.iframeLoading = false;
				}
			}
		},
		// 监听用户信息变化
		'$store.state.userStore.userInfo': {
			handler(newUserInfo) {
				if (newUserInfo && newUserInfo.id) {
					this.syncUserInfoToIframe();
				}
			},
			deep: true
		},
		// 监听会话切换，自动同步 IM 会话信息
		'chat.id': {
			handler(newId, oldId) {
				if (newId !== oldId && this.iframeBridge) {
					console.log('[ChatBox] IM 会话切换，同步新会话信息');
					this.$nextTick(() => {
						this.syncImSessionInfoToIframe();
					});
				}
			}
		}
	}
}
</script>

<style lang="scss">
.chat-box {
	position: relative;
	width: 100%;
	background: #fff;

	// OpenMe 机器人管理面板
	.openme-panel {
		padding: 20px;
		text-align: center;
		background: #f5f7fa;
		border-bottom: 1px solid #e4e7ed;
	}

	// OpenMe iframe 容器
	.openme-iframe-container {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		background: #fff;
		z-index: 100;

		.openme-iframe-header {
			padding: 10px;
			border-bottom: 1px solid #e4e7ed;
			background: #f5f7fa;
			text-align: right;
		}

		.openme-iframe {
			flex: 1;
			width: 100%;
			border: none;
		}
	}

	// 🔥 移动端全屏模式：整个chat-box占据全屏
	&.mobile-fullscreen-mode {
		position: fixed !important;
		top: 0 !important;
		left: 0 !important;
		width: 100vw !important;
		height: 100vh !important;
		overflow: visible !important;
		z-index: 9998;
	}

	// 拖拽覆盖层
	.drag-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(64, 158, 255, 0.08);
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
		z-index: 9999;
		display: flex;
		align-items: center;
		justify-content: center;
		animation: fadeIn 0.2s ease;

		.drag-content {
			text-align: center;
			padding: 40px;
			background: rgba(255, 255, 255, 0.95);
			border-radius: 16px;
			box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
			border: 2px dashed #409EFF;

			i {
				font-size: 64px;
				color: #409EFF;
				display: block;
				margin-bottom: 16px;
			}

			p {
				margin: 8px 0;
				font-size: 18px;
				font-weight: 500;
				color: #303133;

				&.drag-tip {
					font-size: 14px;
					font-weight: normal;
					color: #909399;
					margin-top: 12px;
				}
			}
		}
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
			transform: scale(0.95);
		}
		to {
			opacity: 1;
			transform: scale(1);
		}
	}

	// 重写header布局 - 使用简洁的flex布局
	.chat-header {
		display: flex !important;
		justify-content: space-between !important;
		align-items: center !important;
		padding: 0 16px !important;
		height: 50px;
		border-bottom: var(--im-border);
		background-color: #ededed;
		font-size: var(--im-font-size-larger);
		
		// 左侧标题区
		.chat-title {
			flex: 1;
			display: flex;
			align-items: center;
			gap: 8px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			
			.parent-name {
				color: #606266;
				font-weight: normal;
				padding: 2px 6px;
				border-radius: 4px;
				
				&.clickable {
					cursor: pointer;
					transition: all 0.2s ease;
					
					&:hover {
						background-color: rgba(64, 158, 255, 0.1);
						color: #2AACCC;
					}
				}
			}
			
			.layer-separator {
				color: #909399;
				font-size: 18px;
				margin: 0 4px;
			}
			
			.agent-name {
				color: #2AACCC;
				font-weight: 500;
			}
		}
		
		// 右侧操作区
		.header-right {
			display: flex;
			align-items: center;
			gap: 8px;  // 减小间距，从12px改为8px
			flex-shrink: 0;
			
			// 新的容器结构：分离动画容器和固定头像
			.agent-avatars-container {
				display: flex;
				align-items: center;
				position: relative;
				height: 32px;
				transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
				
				// 侧边栏打开时滑动隐藏
				&.collapsed {
					transform: translateX(100px); // 向右滑出
					opacity: 0;
					pointer-events: none;
					transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
				}
				
				// 可动画的头像容器
				.agent-avatars-animated {
					position: relative;
					height: 32px;
					display: flex;
					align-items: center;
					transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
					overflow: visible;
					
					// 根据头像数量动态计算折叠宽度
					&:not(.expanded) {
						// 1个头像时宽度为0（只显示固定头像）
						&[style*="--avatar-count: 1"] { width: 0; }
						// 2个头像时宽度为20px（1个部分可见）
						&[style*="--avatar-count: 2"] { width: 20px; }
						// 3个头像时宽度为40px（2个部分可见）
						&[style*="--avatar-count: 3"] { width: 40px; }
						// 4个头像时宽度为60px（3个部分可见）
						&[style*="--avatar-count: 4"] { width: 60px; }
						// 5个头像时宽度为80px（4个部分可见）
						&[style*="--avatar-count: 5"] { width: 80px; }
					}
					
					// 展开状态 - 根据头像数量动态计算（间距改为20px）
					&.expanded {
						// 1个头像展开宽度20px
						&[style*="--avatar-count: 1"] { width: 20px; }
						// 2个头像展开宽度40px
						&[style*="--avatar-count: 2"] { width: 40px; }
						// 3个头像展开宽度60px
						&[style*="--avatar-count: 3"] { width: 60px; }
						// 4个头像展开宽度80px
						&[style*="--avatar-count: 4"] { width: 80px; }
						// 5个头像展开宽度100px
						&[style*="--avatar-count: 5"] { width: 100px; }
						
						.agent-avatar-mini {
							pointer-events: auto;
							
							// 展开位置：每个头像间隔20px
							&[style*="--avatar-index: 0"] { left: 0 !important; }
							&[style*="--avatar-index: 1"] { left: 20px !important; }
							&[style*="--avatar-index: 2"] { left: 40px !important; }
							&[style*="--avatar-index: 3"] { left: 60px !important; }
						}
					}
					
					// 动画头像样式
					.agent-avatar-mini {
						position: absolute;
						top: 0;
						transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s ease;
						pointer-events: none; // 默认不可点击
						
						// 折叠位置：每个头像间隔20px
						&[style*="--avatar-index: 0"] { 
							left: 0; 
							z-index: 1;
						}
						&[style*="--avatar-index: 1"] { 
							left: 20px; 
							z-index: 2;
						}
						&[style*="--avatar-index: 2"] { 
							left: 40px; 
							z-index: 3;
						}
						&[style*="--avatar-index: 3"] { 
							left: 60px; 
							z-index: 4;
						}
						
						&:hover {
							transform: scale(1.15);
							z-index: 10;
						}
					}
				}
				
				// 固定的最右侧头像容器
				.agent-avatar-fixed {
					position: relative;
					width: 32px;
					height: 32px;
					margin-left: 4px; // 减小与动画容器的间距
					
					.agent-avatar-mini {
						position: relative;
						z-index: 5;
						cursor: pointer;
						transition: transform 0.2s ease;
						
						&:hover {
							transform: scale(1.15);
							z-index: 10;
						}
					}
				}
			}
			
			// 统一的头像样式
			.agent-avatar-mini {
				width: 32px;
				height: 32px;
				border-radius: 50%; // 纯圆形
				overflow: hidden;
				border: 2px solid white; // 白色2px边框
				box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
				background: white;
				flex-shrink: 0;
				
				img {
					width: 100%;
					height: 100%;
					object-fit: cover; // 100%填充
				}
				
				.agent-avatar-text {
					width: 100%;
					height: 100%;
					display: flex;
					align-items: center;
					justify-content: center;
					background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
					color: white;
					font-weight: 600;
					font-size: 12px;
				}
			}
			
			// 更多按钮
			.header-btn {
				font-size: 18px;
				color: #606266;
				cursor: pointer;
				padding: 4px;
				border-radius: 4px;
				transition: all 0.2s ease;
				
				&:hover {
					background-color: rgba(0, 0, 0, 0.05);
					color: #2AACCC;
				}
			}
		}
	}

	.content-box {
		height: calc(100vh - 50px);
		/* 减去header和footer的高度 */
		display: flex;
		flex-direction: column;
		position: relative;
	}

	// 🔥 移动端全屏模式：所有容器占据整个屏幕
	&.mobile-fullscreen-mode {
		.el-container,
		.el-main {
			position: fixed !important;
			top: 0 !important;
			left: 0 !important;
			width: 100vw !important;
			height: 100vh !important;
			overflow: visible !important;
			padding: 0 !important;
		}

		.content-box {
			position: fixed !important;
			top: 0 !important;
			left: 0 !important;
			width: 100vw !important;
			height: 100vh !important;
			overflow: visible !important;
		}
	}

	// 智能体 iframe 样式
	.agent-iframe-container {
		position: relative;
		width: 100%;
		height: 100%;
		overflow: hidden;
		// 🔑 关键：容器的 z-index 要高于遮罩层（1000），让 iframe 能在上层接收事件
		z-index: 5000;

		// 隐藏但保持 DOM 存在和布局（不使用 display: none）
		&.iframe-hidden {
			visibility: hidden;
			pointer-events: none;
			position: absolute;
			z-index: -1;
		}

		// 移动端全屏模式
		&.mobile-mode {
			position: fixed !important;
			top: 0;
			left: 0;
			width: 100vw !important;
			height: 100vh !important;
			z-index: 9999;
			background: #fff;

			.agent-iframe {
				width: 100vw;
				height: 100vh;
			}

			.iframe-loading-mask {
				position: fixed;
				top: 0;
				left: 0;
				width: 100vw;
				height: 100vh;
			}
		}
	}

	.agent-iframe {
		width: 100%;
		height: 100%;
		border: none;
		// iframe 保持绝对定位占满容器，继承容器的 z-index 层级
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
	}

	// iframe loading 蒙版
	.iframe-loading-mask {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(255, 255, 255, 0.95);
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 10;
		transition: opacity 0.3s ease, visibility 0.3s ease;
	}

	.im-chat-main {
		padding: 0;
		background-color: #fff;
		overflow-y: scroll;
		/* 保持滚动条占位 */
		height: 100%;
		scrollbar-gutter: stable;
		/* 为滚动条保留稳定空间 */

		/* 完全隐藏滚动条但保持滚动功能 */
		&::-webkit-scrollbar {
			width: 8px;
			/* 固定宽度，避免位置窜动 */
			background: transparent;
		}

		&::-webkit-scrollbar-track {
			background: transparent;
		}

		&::-webkit-scrollbar-thumb {
			background: transparent;
		}

		/* 悬停时显示滚动条 */
		&:hover {
			&::-webkit-scrollbar-thumb {
				background: #c1c1c1;
				border-radius: 4px;

				&:hover {
					background: #a8a8a8;
				}
			}
		}

		/* Firefox浏览器 */
		scrollbar-width: thin;
		scrollbar-color: transparent transparent;
		/* 默认透明 */

		&:hover {
			scrollbar-color: #c1c1c1 transparent;
			/* 悬停时显示 */
		}

		.im-chat-box {
			>ul {
				padding: 0 6px 0 20px;

				li {
					list-style-type: none;
				}
			}
		}
	}

	// 输入框分隔条样式
	.input-resizer {
		height: 8px;
		background-color: transparent;
		cursor: row-resize;
		position: relative;
		z-index: 10;
		display: flex;
		align-items: center;
		justify-content: center;

		.resizer-line {
			position: absolute;
			height: 2px;
			width: 100%;
			background-color: transparent;
			transition: background-color 0.2s ease;
		}

		// 三个小点手柄 - 始终显示，横向排列
		.resizer-handle {
			position: relative;
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: center;
			gap: 3px;
			z-index: 1;

			.resizer-dot {
				width: 3px;
				height: 3px;
				border-radius: 50%;
				background-color: rgba(0, 0, 0, 0.2);
				transition: background-color 0.2s ease;
			}
		}

		// 悬停状态
		&.resizer-hovered {
			.resizer-line {
				background-color: #2BACCC;
			}

			.resizer-dot {
				background-color: #2BACCC;
			}
		}

		// 拖拽状态
		&.resizer-dragging {
			.resizer-line {
				background-color: #2BACCC;
				transition: none;
			}

			.resizer-dot {
				background-color: #2BACCC;
				transition: none;
			}
		}
	}

	.im-chat-footer {
		display: flex;
		flex-direction: column;
		padding: 0;

		.chat-tool-bar {
			display: flex;
			position: relative;
			width: 100%;
			height: 36px;
			text-align: left;
			box-sizing: border-box;
			border-top: var(--im-border);
			padding: 4px 2px 2px 8px;

			>div {
				font-size: 22px;
				cursor: pointer;
				line-height: 30px;
				width: 30px;
				height: 30px;
				text-align: center;
				border-radius: 2px;
				margin-right: 8px;
				color: #999;
				transition: 0.3s;

				&.chat-tool-active {
					font-weight: 600;
					color: var(--im-color-primary);
					background-color: #ddd;
				}
			}

			>div:hover {
				color: #333;
			}
		}

		.send-content-area {
			position: relative;
			display: flex;
			flex-direction: column;
			flex: 1;
			height: 0;
			background-color: white !important;
			padding: 0;
			cursor: text;

			.send-btn-area {
				padding: 10px;
				position: absolute;
				bottom: 4px;
				right: 6px;
			}
		}
	}

	.chat-group-side-box {
		border-left: var(--im-border);
		//animation: rtl-drawer-in .3s 1ms;
	}
	
	// 统一侧边栏样式（已弃用，改用浮动面板）
	.chat-side-panel {
		border-left: 2px solid #e4e7ed;
		background: #fafafa;
		animation: rtl-drawer-in 0.3s ease;
		box-shadow: -2px 0 8px rgba(0, 0, 0, 0.05);
		display: flex;
		flex-direction: column;

		.side-tabs {
			height: 100%;
			display: flex;
			flex-direction: column;

			.el-tabs__header {
				margin: 0;
				border-bottom: 1px solid #e4e7ed;
			}

			.el-tabs__content {
				flex: 1;
				overflow: hidden;
				padding: 0;

				.el-tab-pane {
					height: 100%;
					overflow: auto;
				}
			}
		}
	}

	// 浮动侧边栏面板 - 不占用布局空间
	.floating-side-panel {
		position: absolute;
		top: 50px; // 紧贴header下方
		right: 0;
		bottom: 0;
		width: 360px;
		background: #ffffff;
		border-left: 2px solid #e4e7ed;
		box-shadow: -4px 0 16px rgba(0, 0, 0, 0.1);
		z-index: 6000; // 提高z-index，确保在iframe之上
		display: flex;
		flex-direction: column;

		.side-tabs {
			height: 100%;
			display: flex;
			flex-direction: column;
			padding-top: 0;

			.el-tabs__header {
				margin: 0;
				border-bottom: 1px solid #e4e7ed;
				background: #fafafa;
			}

			.el-tabs__content {
				flex: 1;
				overflow: hidden;
				padding: 0;

				.el-tab-pane {
					height: 100%;
					overflow: auto;
				}
			}
		}
	}

	// Vue transition 动画类
	.slide-panel-enter-active {
		animation: slideInRight 0.3s ease;
	}
	
	.slide-panel-leave-active {
		animation: slideOutRight 0.3s ease;
	}

	// 浮动面板滑入动画
	@keyframes slideInRight {
		from {
			transform: translateX(100%);
			opacity: 0;
		}
		to {
			transform: translateX(0);
			opacity: 1;
		}
	}
	
	// 浮动面板滑出动画
	@keyframes slideOutRight {
		from {
			transform: translateX(0);
			opacity: 1;
		}
		to {
			transform: translateX(100%);
			opacity: 0;
		}
	}
	
	// 保持原有样式兼容性
	.chat-agent-side-box {
		border-left: 2px solid #e4e7ed;
		background: #fafafa;
		animation: rtl-drawer-in 0.3s ease;
		box-shadow: -2px 0 8px rgba(0, 0, 0, 0.05);
	}
	
	// 智能体悬停面板样式
	.agent-hover-panel {
		position: fixed;
		z-index: 6500; // 提高z-index，确保在iframe和侧面板之上
		width: 320px;
		animation: slideDown 0.2s ease-out;
		// 禁用文本选择
		user-select: none;
		-webkit-user-select: none;
		-moz-user-select: none;
		-ms-user-select: none;
		// 确保指针事件正常
		pointer-events: auto;
		
		// 指向箭头 - 优化与面板的融合
		.panel-arrow {
			position: absolute;
			top: -5px;  // 缩小高度，从-6px改为-5px
			left: 50%;
			transform: translateX(calc(-50% + var(--arrow-offset, 0px)));
			width: 10px;  // 缩小宽度，从12px改为10px
			height: 5px;  // 缩小高度，从6px改为5px
			
			&::before {
				content: '';
				position: absolute;
				top: 0;
				left: 50%;
				transform: translateX(-50%) rotate(45deg);
				width: 8px;  // 缩小尺寸，从10px改为8px
				height: 8px;  // 缩小尺寸，从10px改为8px
				// 与面板保持一致的背景
				background: linear-gradient(135deg, 
					rgba(255, 255, 255, 0.95), 
					rgba(250, 251, 252, 0.98));
				border-left: 1px solid rgba(228, 231, 237, 0.8);
				border-top: 1px solid rgba(228, 231, 237, 0.8);
				backdrop-filter: blur(10px);
				-webkit-backdrop-filter: blur(10px);
			}
			
			// 遮盖下边框线，实现完美融合
			&::after {
				content: '';
				position: absolute;
				bottom: -1px;
				left: 50%;
				transform: translateX(-50%);
				width: 9px;  // 相应缩小，从11px改为9px
				height: 2px;
				// 使用渐变背景遮盖
				background: linear-gradient(135deg, 
					rgba(255, 255, 255, 0.95), 
					rgba(250, 251, 252, 0.98));
			}
		}
		
		// 卡片主体样式 - 扁平化设计带磨砂效果
		.chat-agent-card {
			border: 1px solid rgba(228, 231, 237, 0.8);
			border-radius: 8px;
			// 移除阴影，保持扁平
			box-shadow: none;
			// 添加渐变和磨砂效果
			background: linear-gradient(135deg, 
				rgba(255, 255, 255, 0.95) 0%, 
				rgba(250, 251, 252, 0.98) 50%,
				rgba(248, 249, 251, 0.95) 100%);
			backdrop-filter: blur(10px);
			-webkit-backdrop-filter: blur(10px);
			position: relative;
			
			// 添加微妙的内发光效果
			&::before {
				content: '';
				position: absolute;
				inset: 0;
				border-radius: 8px;
				background: linear-gradient(135deg,
					rgba(64, 158, 255, 0.03) 0%,
					transparent 50%,
					rgba(103, 194, 58, 0.02) 100%);
				pointer-events: none;
			}
		}
	}
	
	@keyframes slideDown {
		from {
			opacity: 0;
			transform: translateY(-10px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}
	
	@keyframes pulse {
		0%, 100% {
			opacity: 1;
		}
		50% {
			opacity: 0.5;
		}
	}
}

// 🔥 任务详情面板样式
.task-detail-drawer {
	.el-drawer__body {
		padding: 0;
		height: 100%;
	}
}

.task-detail-panel {
	height: 100%;
	display: flex;
	flex-direction: column;
	background: #f8f9fa;

	.panel-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 16px 20px;
		background: #ffffff;
		border-bottom: 1px solid #e4e7ed;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

		.header-info {
			flex: 1;

			.panel-title {
				margin: 0 0 4px 0;
				font-size: 16px;
				font-weight: 600;
				color: #303133;
				display: flex;
				align-items: center;
				gap: 8px;

				i {
					color: #409eff;
				}
			}

			.task-info {
				display: flex;
				align-items: center;
				gap: 12px;
				font-size: 12px;
				color: #909399;

				.agent-name {
					background: #f0f9ff;
					color: #409eff;
					padding: 2px 8px;
					border-radius: 4px;
					font-weight: 500;
				}

				.task-id {
					font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
				}
			}
		}

		.close-btn {
			font-size: 18px;
			color: #909399;
			padding: 8px;

			&:hover {
				color: #f56c6c;
				background: #fef0f0;
			}
		}
	}

	.iframe-container {
		flex: 1;
		position: relative;
		background: #ffffff;

		.task-detail-iframe {
			width: 100%;
			height: 100%;
			border: none;
			background: #ffffff;
		}

		.iframe-loading {
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			background: rgba(255, 255, 255, 0.9);
			z-index: 10;
		}
	}
}
</style>
